import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  Calendar,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Pin,
  ArrowRightLeft,
  Sparkles,
  RotateCcw,
  X,
  Users,
  History,
  CheckCircle2,
  Map as MapIcon,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { TechAvatar } from "@/components/TechAvatar";
import {
  formatTime,
  resolveWindowAndPriority,
  extractJobPreview,
  displayCustomerName,
} from "@/lib/format";
import {
  useBoardDispatches,
  useActiveTechs,
  groupByTech,
  isPseudoDispatch,
  useRoutePlan,
  useSaveRoutePlan,
  useDispatchAssignments,
  useSetAssignment,
  useTechSkills,
  useTechSeniority,
  useLocationNotes,
  windowColorClasses,
  timeBand,
} from "./api";
import {
  recommendForTech,
  deriveRequiredSkills,
  deriveRequestedTech,
  deriveRequestedTechs,
  deriveAttrLock,
  deriveExcludedTechs,
  type RecoTech,
  type RecoStop,
} from "@/lib/recommendations";
import { DateNavigator } from "./DateNavigator";
import { OncallDateChip } from "@/features/team/OncallChip";
import { TechColumn } from "./TechColumn";
import { useScheduledForBoard, usePendingTicketById, useMoveDispatch, type PendingTicket } from "./queue";
import { DispatchDetailModal, DispatchDetailBody } from "./DispatchDetailModal";
import { PegasusJobDetailModal, PegasusJobDetailBody } from "./PegasusJobDetail";
import { NewDispatchLauncher } from "./NewDispatchLauncher";
import { JobBoardOverview } from "./JobBoardOverview";
import {
  MovePreview,
  ReorderPreview,
  type PendingMove,
  type PendingReorder,
} from "./MovePreview";
import { useBoardConflicts, BoardConflictsProvider } from "./conflicts";
import {
  useTechAvailability,
  availabilityByTech,
  AVAIL_KIND_LABEL,
  type AvailEvent,
} from "./availability";
import { AvailabilityModal } from "./AvailabilityModal";
import { RebuildDayModal } from "./RebuildDayModal";
import { PendingStrip } from "./PendingStrip";
import { DispatchMap, MAP_PALETTE, type MapPoint } from "@/components/DispatchMap";
import {
  optimizeBanded,
  routeMiles,
  driveMinutes,
  insertionCostMiles,
  usableCoords,
} from "@/lib/route";
import {
  planWholeDay,
  type OptiStop,
  type WholeDayPlan,
} from "@/lib/boardOptimize";
import {
  WholeDayOptimizeModal,
  type WholeDayCommit,
} from "./WholeDayOptimizeModal";
import { useRouteDrive } from "@/lib/driveTimes";
import { useRoadPath } from "@/lib/roadPath";
import {
  STATUS_DONE,
  STATUS_ACTIVE,
  STATUS_PENDING,
  type Dispatch,
} from "@/lib/types";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/cn";
import { useOrgSettingNumber } from "@/lib/orgSettings";

export type ViewMode = "active" | "history" | "all";
export type BoardMode = "service" | "install" | "plumbing" | "nc";
// Each board lens shows ONE trade's roster (Seth 2026-07-08: Install shows only
// installers, Service only service techs, etc.). ESC dept code → the lens.
const DEPT_FOR_MODE: Record<BoardMode, string> = {
  service: "03", // Service
  install: "01", // Install / Sales
  plumbing: "08", // Plumbing
  nc: "02", // New Construction
};
// Remember the last lens the dispatcher chose (e.g. the install coordinator
// always opens to Install) across reloads.
const BOARD_MODE_STORAGE_KEY = "pegasus.board.mode";
function loadSavedBoardMode(): BoardMode {
  try {
    const v = localStorage.getItem(BOARD_MODE_STORAGE_KEY);
    if (v === "service" || v === "install" || v === "plumbing" || v === "nc") return v;
  } catch {
    // localStorage unavailable (private mode etc.)
  }
  return "service";
}
/**
 * BOARD-MAP-STATES (Seth 2026-09-29: "maybe we don't need column and cockpit…
 * cockpit by default and then you can enable/disable the map… detach the map
 * and move it around and then it essentially becomes column view"): the three
 * old layouts (Columns / Map / Cockpit) collapse into ONE board with a map
 * that has four states —
 *   docked  → the cockpit arrangement (map pane beside the lanes + strip)
 *   float   → full-width lanes + a draggable/resizable map panel on top
 *   window  → full-width lanes + the map in its OWN browser window (drag it
 *             to the second monitor; synced live over a BroadcastChannel)
 *   off     → just the lanes (the old Columns view)
 * `Layout` survives internally as a derived value so the cockpit strip /
 * modal / fit-width behaviors keep their meaning: docked = cockpit UX,
 * everything else = columns UX.
 */
type Layout = "columns" | "cockpit";
// BOARD-MAP-02 (Seth 2026-10-06): Floating retired — "Docked, window, off work."
type MapMode = "docked" | "window" | "off";

const MAP_MODE_KEY = "pegasus.board.map";

function loadSavedMapMode(): MapMode {
  try {
    const v = localStorage.getItem(MAP_MODE_KEY);
    if (v === "docked" || v === "window" || v === "off") return v;
    if (v === "float") return "docked"; // retired mode (BOARD-MAP-02) — fall back to the cockpit
    // Migrate the retired three-layout setting (pegasus.board.layout).
    const old = localStorage.getItem("pegasus.board.layout");
    if (old === "columns") return "off";
    if (old === "map" || old === "cockpit") return "docked";
  } catch {
    // localStorage unavailable (private mode etc.) — fall through to default
  }
  return "docked"; // Seth: "cockpit by default"
}


/**
 * Board density (Seth, 2026-06-09 — "dispatchers like to see the board in its
 * entirety… show sort of what ESC looks like"): `compact` = ESC-EDB-style
 * full-color blocks (name · city · window · priority, no icon chrome) so the
 * WHOLE roster fits on screen; `cards` = the richer comfortable cards.
 * Selecting a tech (header click / map chip) still expands to just that tech.
 */
type Density = "compact" | "cards";
const DENSITY_STORAGE_KEY = "pegasus.board.density";
function loadSavedDensity(): Density {
  try {
    const v = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (v === "compact" || v === "cards") return v;
  } catch {
    // fall through
  }
  return "cards";
}

const COCKPIT_HEIGHT_KEY = "pegasus.board.cockpit.detailHeight";

function loadCockpitHeight(): number {
  try {
    const v = Number(localStorage.getItem(COCKPIT_HEIGHT_KEY));
    if (Number.isFinite(v) && v >= 140 && v <= 600) return v;
  } catch {
    // ignore
  }
  return 230;
}

// Apply a target order (planned / optimized) while holding pinned stops in their
// current slots — the unpinned ones fill the gaps in target order.
function applyOrderWithPins(
  current: string[],
  target: string[],
  pinned: Set<string>,
): string[] {
  const slots: (string | null)[] = current.map((id) =>
    pinned.has(id) ? id : null,
  );
  const fill = target.filter((id) => !pinned.has(id));
  let fi = 0;
  for (let i = 0; i < slots.length; i++) {
    if (slots[i] === null) slots[i] = fill[fi++] ?? null;
  }
  return slots.filter((x): x is string => x !== null);
}

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** A labeled cluster in the board control bar — the when/what/how regroup.
 *  Pure presentation: a hairline pill with a tiny caption, zero logic. */
function ControlCluster({ label, children }: { label: string; children: ReactNode }) {
  // BOARD-HD: each cluster reads as one instrument on the console — a white
  // panel with a quiet caption, instead of a grey wash that blended into the
  // page. Same one-row height (the cockpit keeps every vertical pixel).
  return (
    <div data-glass="capsule" className="inline-flex items-center gap-2 rounded-full border py-1 pl-2.5 pr-1.5">
      <span className="select-none pl-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</span>
      {children}
    </div>
  );
}

/** ESC-parallel view filter for Pegasus dispatches (Seth 2026-08-12): ran/
 *  completed calls LEAVE the active board — the day reads as what's left —
 *  and live under History. Same rule ESC dispatches have always followed. */
function pegasusVisibleForView(j: PendingTicket, view: ViewMode): boolean {
  const done = j.status === "completed";
  if (view === "active") return !done;
  if (view === "history") return done;
  return true;
}

/** Which board work-type lens a Pegasus ticket belongs to. ESC dispatches are
 *  always service; Pegasus visits carry a role (install today; plumbing / new
 *  construction light up as those flows land). */
function workTypeOf(j: PendingTicket): BoardMode {
  const r = (j.visit_role ?? "").toLowerCase();
  if (r === "install") return "install";
  if (r.includes("plumb")) return "plumbing";
  if (r === "nc" || r.includes("construction")) return "nc";
  return "service";
}

/**
 * Dispatch Board - the daily-driver tool that replaces ESC's EDB.
 *
 * Layout:
 *   - Header: date navigator + stats
 *   - Body: horizontal scroll of per-tech columns
 *   - Each column: tech name + stack of dispatch blocks (colored by window)
 *   - Click a block: detail modal with full info + jump to Customer 360
 *
 * URL-driven date: ?date=2026-05-19 — bookmark or share a specific day.
 * URL-driven tech filter: ?tech=0008 — single-tech focused view (from
 * clicking a tech name in Today modals etc).
 */
export function BoardView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const initialDate = searchParams.get("date") ?? todayISO();
  const techFilter = searchParams.get("tech");

  const [date, setDate] = useState(initialDate);

  // KEEP THE AMBIENT BOARD ON TODAY (Seth) — if Pegasus is left open overnight
  // or for days (the board sits on a monitor untouched, or the tab sleeps), the
  // board's date would otherwise stay frozen on the day it loaded. We roll it
  // forward when the calendar day changes, but ONLY while the board is
  // "following today" — i.e. the user hasn't navigated to a specific day. Any
  // deliberate date pick flips this off so we never yank someone off the day
  // they chose; picking "Today" turns it back on.
  const followingToday = useRef(initialDate === todayISO());
  const selectDate = useCallback((d: string) => {
    followingToday.current = d === todayISO();
    setDate(d);
  }, []);
  useEffect(() => {
    const rollIfStale = () => {
      // Functional update so the timer never reads a stale `date`.
      setDate((prev) => (followingToday.current && prev !== todayISO() ? todayISO() : prev));
    };
    const id = window.setInterval(rollIfStale, 30_000); // untouched wall monitor
    const onVisible = () => { if (document.visibilityState === "visible") rollIfStale(); };
    document.addEventListener("visibilitychange", onVisible); // tab waking from sleep
    window.addEventListener("focus", rollIfStale);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", rollIfStale);
    };
  }, []);

  const [selected, setSelected] = useState<Dispatch | null>(null);
  // Pegasus-native job selection — parallel to `selected` (the ESC stop). Both
  // feed the cockpit strip / the popup; only one shows at a time (Seth
  // 2026-06-24: a native card should behave like an ESC card).
  const [selectedTicket, setSelectedTicket] = useState<PendingTicket | null>(null);
  const selectDispatch = useCallback((d: Dispatch | null) => {
    setSelectedTicket(null);
    setSelected(d);
  }, []);
  const selectTicket = useCallback((t: PendingTicket | null) => {
    setSelected(null);
    setSelectedTicket(t);
  }, []);
  // Open-in-WINDOW (Seth 2026-06-25): double-click / right-click "Open dispatch"
  // pop the MODAL on every layout — cockpit + map included — not the bottom
  // strip. PegasusBoardBlock fires a `pegasus:open-dispatch` window event (avoids
  // threading a callback through every board layer); the board catches it here.
  const [windowTicket, setWindowTicket] = useState<PendingTicket | null>(null);
  useEffect(() => {
    const onOpen = (e: Event) => {
      const job = (e as CustomEvent<PendingTicket>).detail;
      if (job) {
        setSelected(null);
        setWindowTicket(job);
      }
    };
    window.addEventListener("pegasus:open-dispatch", onOpen as EventListener);
    return () => window.removeEventListener("pegasus:open-dispatch", onOpen as EventListener);
  }, []);
  // BOARD-DBLCLICK-NEW (Seth 2026-08-12; hit-area rework 2026-08-13: "double
  // clicking is near impossible… I was hoping anywhere in the open/grey area
  // would work" — cockpit especially). ONE handler on the board's grey body:
  // any double-click that isn't on a card, a control, the map, or the cockpit
  // strip resolves to the NEAREST tech column by X and starts a new dispatch
  // with that tech + this board day prefilled. So the grey below/beside a
  // tech's lane, the gaps between lanes, and the empty space inside a lane all
  // work — you never have to hit the white card itself.
  const [newDispatch, setNewDispatch] = useState<{ empno: string | null; name: string | null; date: string | null } | null>(null);
  const onBodyDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      const t = e.target as HTMLElement;
      // Cards ([data-key] wraps every column item), interactive controls, the
      // Leaflet map (dbl-click = zoom), and opted-out panes keep their clicks.
      if (t.closest("[data-key],button,a,input,textarea,select,.leaflet-container,[data-no-dblclick]")) return;
      const cols = Array.from(
        (e.currentTarget as HTMLElement).querySelectorAll<HTMLElement>("[data-drop-empno]"),
      );
      if (!cols.length) return;
      let best: HTMLElement | null = null;
      let bestDist = Infinity;
      let unionL = Infinity;
      let unionR = -Infinity;
      for (const c of cols) {
        const r = c.getBoundingClientRect();
        unionL = Math.min(unionL, r.left);
        unionR = Math.max(unionR, r.right);
        const dist = e.clientX < r.left ? r.left - e.clientX : e.clientX > r.right ? e.clientX - r.right : 0;
        if (dist < bestDist) {
          bestDist = dist;
          best = c;
        }
      }
      // Stay within the columns region (+ slack): a dbl-click way off under the
      // cockpit map shouldn't book the far-right tech.
      if (!best || e.clientX < unionL - 48 || e.clientX > unionR + 48) return;
      setNewDispatch({
        empno: best.dataset.dropEmpno ?? null,
        name: best.dataset.techName ?? null,
        date,
      });
    },
    [date],
  );
  const [viewMode, setViewMode] = useState<ViewMode>("active");
  const [boardMode, setBoardMode] = useState<BoardMode>(loadSavedBoardMode);
  useEffect(() => {
    try { localStorage.setItem(BOARD_MODE_STORAGE_KEY, boardMode); } catch { /* localStorage unavailable */ }
  }, [boardMode]);
  const [mapMode, setMapMode] = useState<MapMode>(loadSavedMapMode);
  // COCKPIT-WINDOW-01 + COCKPIT-ALWAYS (Seth 2026-10-08: the bottom detail
  // strip "disappears if you... put the map in a new window", then "I would
  // like it to also be there if the map is turned off"): the cockpit — strip,
  // fit-width lanes, resizable bottom detail bar — is now the ONE board
  // layout in every map mode. The map pane itself is the only thing the
  // toggle changes: beside the board (Docked), its own window (Window), or
  // gone (Off). Single-click always fills the bottom bar; double-click still
  // opens the full dispatch. The old columns branch below is retired in
  // place (unreachable) until cockpit-everywhere has lived a few days.
  const layout: Layout = "cockpit";
  const mapWinRef = useRef<Window | null>(null);
  const [density, setDensity] = useState<Density>(loadSavedDensity);
  // "Optimize day" from the Columns view jumps to Map + auto-opens the optimizer
  // (route optimization lives on the map). Consumed by BoardMapView's effect.
  const [pendingOptimize, setPendingOptimize] = useState(false);

  // Two-window remote control (Seth / SCHED-AI-01a): another Pegasus window
  // (e.g. the 360's time suggestions) can ask the AMBIENT board window to jump
  // to a date — "actual eyes on" without navigating the working window.
  useEffect(() => {
    // Named window (Seth 2026-06-11, the new-tab complaint): lets any
    // "Show on board" REUSE this exact tab via window.open(..., "pegasus-board")
    // when the channel ping gets no answer (e.g. Chrome froze this tab in the
    // background) — one board window forever, never a pile of tabs.
    try {
      window.name = "pegasus-board";
    } catch {
      // ignore
    }
    const ch = new BroadcastChannel("pegasus-board");
    ch.onmessage = (e) => {
      const msg = e.data as { type?: string; date?: string; tech?: string };
      if (msg?.type === "board.show-date" && typeof msg.date === "string") {
        selectDate(msg.date);
        ch.postMessage({ type: "board.ack" });
        // Let the dispatcher's eyes find it: flash the date stepper area.
        window.focus();
      }
      if (msg?.type === "board.ping") ch.postMessage({ type: "board.pong" });
    };
    return () => ch.close();
  }, [selectDate]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (date !== todayISO()) params.date = date;
    if (techFilter) params.tech = techFilter;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Remember the dispatcher's map arrangement across sessions.
  useEffect(() => {
    try {
      localStorage.setItem(MAP_MODE_KEY, mapMode);
    } catch {
      // ignore (private mode / storage disabled)
    }
  }, [mapMode]);
  // Window mode: open (or refocus) the map's own browser window. The
  // dispatcher drags it to the second monitor; closing it is fine — picking
  // Window again reopens it.
  const openMapWindow = () => {
    const w = mapWinRef.current;
    if (w && !w.closed) {
      w.focus();
      return;
    }
    mapWinRef.current = window.open(
      `/board/map?date=${date}`,
      "pegasus-board-map",
      "popup=yes,width=1040,height=800",
    );
  };
  useEffect(() => {
    if (mapMode === "window") openMapWindow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMode]);
  useEffect(() => {
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, density);
    } catch {
      // ignore
    }
  }, [density]);

  // Toggle the single-tech filter (driven by the map legend chips). Clicking
  // the already-active tech clears the filter.
  const toggleTech = (empno: string) => {
    const params: Record<string, string> = {};
    if (date !== todayISO()) params.date = date;
    if (techFilter !== empno) params.tech = empno;
    setSearchParams(params, { replace: true });
  };

  const { data, isLoading, isError, error } = useBoardDispatches(date);
  const { data: activeTechs } = useActiveTechs(DEPT_FOR_MODE[boardMode]);
  const { data: assignments } = useDispatchAssignments();

  // Realtime: when sync writes new dispatch data to Supabase, refresh
  // the board immediately instead of waiting for the 60s background poll.
  useRealtimeRefresh(
    ["mirror_disptech", "mirror_dispatch"],
    [["board_dispatches"]],
  );

  // Apply Pegasus-native cross-tech reassignments (BUILD-20b) so a moved call
  // shows under its new tech everywhere (columns, map, editor).
  const effectiveRows = (data ?? []).map((r) => {
    const t = assignments?.[r.dispatch];
    return t && t !== r.tech_empno ? { ...r, tech_empno: t } : r;
  });

  // Filter rows by view mode (ESC parallel: Active / History dropdown)
  // Work-type lens (BOARD-MODE): ESC dispatches are all "service", so they only
  // show in the Service lens; Install / Plumbing / NC show their own techs +
  // Pegasus visits (BoardBody + Map filter pegasusJobs by workTypeOf).
  const escVisibleForMode = boardMode === "service";
  const filteredByMode = effectiveRows.filter((r) => {
    if (!escVisibleForMode) return false;
    const isDone = STATUS_DONE.has(r.status ?? "");
    if (viewMode === "active") return !isDone;
    if (viewMode === "history") return isDone;
    return true;
  });

  // Left/right arrow nav through the open card detail (Seth 2026-06-24): when a
  // dispatch detail (popup / cockpit strip) is open, ← / → step to the prev/next
  // card of the SAME kind in board reading order (tech lane, then time). Wraps.
  const pegasusForNav = (useScheduledForBoard(date).data ?? []).filter((j) => pegasusVisibleForView(j, viewMode));

  // Deep-link: `?focus={pegasus dispatch id}` opens that call's full detail
  // window straight away — e.g. clicking a scheduled call on the customer 360
  // drops you right into it on the board. Captured ONCE on mount into state
  // because the board re-syncs ?date into the URL and would otherwise strip
  // ?focus before we can use it. We fetch the dispatch by its OWN id (not the
  // board-day list, which can be served stale-empty from cache right after a
  // client-side nav) so the window opens reliably, then consume (state, not URL)
  // so closing it stays closed.
  const [pendingFocus, setPendingFocus] = useState<string | null>(() => searchParams.get("focus"));
  // The 360 hands us the full dispatch via router state so the window opens
  // INSTANTLY (no refetch — the board fires a flood of queries on mount and a
  // by-id fetch can sit behind them for ~10s). A direct /board?focus=ID link
  // (no state) falls back to fetching the dispatch by its own id.
  const focusFromState = useState<PendingTicket | null>(
    () => (location.state as { focusJob?: PendingTicket } | null)?.focusJob ?? null,
  )[0];
  const fetchedFocus = usePendingTicketById(focusFromState ? null : pendingFocus).data;
  const focusJob = focusFromState ?? fetchedFocus ?? null;
  useEffect(() => {
    if (!pendingFocus || !focusJob) return;
    setSelected(null);
    setWindowTicket(focusJob);
    setPendingFocus(null);
  }, [pendingFocus, focusJob]);

  // CARRYOVER count — Pegasus calls riding forward from an earlier day (still open).
  // (On a non-today date the hook returns exact-date rows, so this is naturally 0.)
  const carriedCount = pegasusForNav.filter(
    (j) => j.scheduled_date && j.scheduled_date < date,
  ).length;
  const navRows = techFilter ? filteredByMode.filter((r) => r.tech_empno === techFilter) : filteredByMode;
  const escSig = navRows.map((r) => r.dispatch).join(",");
  const pegSig = pegasusForNav.map((j) => j.id).join(",");
  const escOrdered = useMemo(
    () =>
      navRows
        .filter((r) => !isPseudoDispatch(r))
        .slice()
        .sort((a, b) =>
          a.tech_empno !== b.tech_empno
            ? a.tech_empno.localeCompare(b.tech_empno)
            : (a.tpromtime ?? "~").localeCompare(b.tpromtime ?? "~"),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [escSig, techFilter],
  );
  const pegOrdered = useMemo(
    () =>
      (techFilter ? pegasusForNav.filter((j) => j.assigned_empno === techFilter) : pegasusForNav)
        .slice()
        .sort((a, b) => {
          const ae = a.assigned_empno ?? "~";
          const be = b.assigned_empno ?? "~";
          if (ae !== be) return ae.localeCompare(be);
          return (a.scheduled_time ?? a.scheduled_window ?? "~").localeCompare(b.scheduled_time ?? b.scheduled_window ?? "~");
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pegSig, techFilter],
  );
  useEffect(() => {
    if (!selected && !selectedTicket) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return; // don't hijack typing
      const dir = e.key === "ArrowRight" ? 1 : -1;
      if (selectedTicket && pegOrdered.length) {
        const i = pegOrdered.findIndex((j) => j.id === selectedTicket.id);
        if (i >= 0) { e.preventDefault(); selectTicket(pegOrdered[(i + dir + pegOrdered.length) % pegOrdered.length]); }
      } else if (selected && escOrdered.length) {
        const i = escOrdered.findIndex((d) => d.dispatch === selected.dispatch);
        if (i >= 0) { e.preventDefault(); selectDispatch(escOrdered[(i + dir + escOrdered.length) % escOrdered.length]); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, selectedTicket, escOrdered, pegOrdered, selectDispatch, selectTicket]);

  // Tech availability for the board date (TECH-AVAIL-01): OUT banners, timed
  // meeting/errand chips, and the "Tech is out" conflict on their calls.
  const availability = useTechAvailability(date).data ?? [];
  const availByTech = availabilityByTech(availability);
  const outEmpnos = [...availByTech.entries()]
    .filter(([, v]) => v.out)
    .map(([empno]) => empno);
  const [availModal, setAvailModal] = useState<{
    empno: string;
    edit: AvailEvent | null;
  } | null>(null);
  const openAddAvailability = (empno: string) =>
    setAvailModal({ empno, edit: null });
  const openEditAvailability = (e: AvailEvent) =>
    setAvailModal({ empno: e.empno, edit: e });

  // Rebuild-the-day (ROUTE-05 first slice): opened from an OUT column's button,
  // or automatically right after marking a tech out for THIS board date while
  // he still has open calls — the consequence surfaces where the action was
  // taken (research/tech-availability-design.md §4A).
  const [rebuildFor, setRebuildFor] = useState<string | null>(null);
  const hasOpenCalls = (empno: string) =>
    filteredByMode.some(
      (r) =>
        r.tech_empno === empno &&
        !isPseudoDispatch(r) &&
        !STATUS_DONE.has(r.status ?? ""),
    );
  const onAvailabilityAdded = (v: {
    empnos: string[];
    allDay: boolean;
    startDate: string;
    endDate: string;
  }) => {
    if (!v.allDay || v.startDate > date || v.endDate < date) return;
    const needsRebuild = v.empnos.find(hasOpenCalls);
    if (needsRebuild) setRebuildFor(needsRebuild);
  };

  // Per-call conflict badges (wrong tech / not qualified / requested / tech out)
  // — shown on the board cards via context so DispatchBlock reads its own.
  const conflicts = useBoardConflicts(filteredByMode, outEmpnos);

  // WINDOW-MAP: the popped-out map is a dumb renderer — this window streams it
  // rows + filters and takes selections back over a BroadcastChannel, so the
  // board and the map can sit on different monitors and stay one brain.
  useEffect(() => {
    if (mapMode !== "window") return;
    const ch = new BroadcastChannel("pegasus-board-map");
    const send = () =>
      ch.postMessage({
        type: "ctx",
        date,
        boardMode,
        viewMode,
        techFilter,
        rows: filteredByMode,
        selectedKey: selected?.dispatch ?? null,
      });
    send();
    ch.onmessage = (e) => {
      const m = e.data as { type?: string; row?: Dispatch; ticket?: PendingTicket; empno?: string };
      if (m?.type === "hello") send();
      else if (m?.type === "select") selectDispatch(m.row ?? null);
      else if (m?.type === "selectTicket") selectTicket(m.ticket ?? null);
      else if (m?.type === "toggleTech" && m.empno) toggleTech(m.empno);
    };
    return () => ch.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMode, date, boardMode, viewMode, techFilter, filteredByMode, selected]);

  return (
    // DESIGN-CALM-01: data-dense keeps the board's card grid out of the office
    // type floor — its density is the dispatcher's, not the design pass's.
    <div className="flex h-full flex-col" data-dense>
      {/* Header — ONE row: controls left, stats filling the space to the
          right (Seth: cockpit dispatchers need every vertical pixel for the
          board tiles; the old stacked header + stats rows pushed them down). */}
      {/* DESKTOP-GLASS-01: the console sits on the canvas; each cluster is a glass capsule. */}
      <div className="px-6 py-2">
        {/* Design-review Wave-1: the seven loose control clusters regrouped into
            WHEN (date) · SHOW (which work) · VIEW (how it's drawn) — labeled
            clusters, zero logic change. Actions + status stay standalone. */}
        <div className="flex flex-wrap items-center gap-3">
          <ControlCluster label="When">
            <DateNavigator value={date} onChange={selectDate} />
            {/* ONCALL-SCHED-01a: the day's on-call pair, from the published calendar. */}
            <OncallDateChip date={date} />
          </ControlCluster>
          <ControlCluster label="Show">
            <BoardModeToggle value={boardMode} onChange={setBoardMode} />
            {techFilter && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
                One tech
                <button type="button" onClick={() => setSearchParams({ date }, { replace: true })} className="text-brand-600 hover:text-brand-900">✕</button>
              </span>
            )}
          </ControlCluster>
          <ControlCluster label="View">
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            <MapModeToggle
              value={mapMode}
              onChange={(m) => {
                // Re-picking Window refocuses (or reopens) the map window.
                if (m === "window" && mapMode === "window") openMapWindow();
                setMapMode(m);
              }}
            />
            <DensityToggle value={density} onChange={setDensity} />
          </ControlCluster>
          {!techFilter && (
            <button
              type="button"
              onClick={() => {
                if (mapMode !== "docked") setMapMode("docked");
                setPendingOptimize(true);
              }}
              title="Optimize the whole day's routes (opens on the map)"
              className="inline-flex h-8 items-center gap-1.5 rounded-full bg-brand-700 px-3.5 text-[12.5px] font-semibold text-white shadow-e1 transition-all duration-150 ease-out-quart hover:bg-brand-800 hover:shadow-e2 active:scale-[0.98]"
            >
              <Sparkles className="h-3.5 w-3.5" /> Optimize day
            </button>
          )}
          {/* Right side = carried-over flag + the day's score, as ONE group, so
              the score never gets pushed to a second row just because a
              carried call exists (every vertical pixel is the board's). */}
          {(carriedCount > 0 || (data && data.length > 0)) && (
            <div className="ml-auto flex flex-wrap items-center gap-3">
              {carriedCount > 0 && (
                <div
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 text-[12.5px] font-semibold text-amber-800"
                  title={`${carriedCount} call${carriedCount === 1 ? "" : "s"} carried over from an earlier day — not completed yet. They're flagged amber in each tech's column; follow up.`}
                >
                  <History className="h-3.5 w-3.5" />
                  {carriedCount} carried over
                </div>
              )}
              {data && data.length > 0 && <BoardStats rows={effectiveRows} techFilter={techFilter} techCount={techFilter ? 1 : (activeTechs?.length ?? 0)} />}
            </div>
          )}
        </div>
      </div>

      {/* Schedule queue (BUILD-19 / TICKET-01): pending tickets in lanes,
          collapsed to one bar until something needs attention. */}
      <PendingStrip />

      {/* Body — scrolls VERTICALLY too: on short screens the columns are
          taller than the viewport and every card must stay reachable
          (Seth, cockpit on a smaller monitor). Double-click any open/grey
          space → new dispatch on the nearest tech (BOARD-DBLCLICK-NEW). */}
      <div
        className="flex-1 overflow-x-auto overflow-y-auto bg-slate-50 px-6 py-3"
        onDoubleClick={onBodyDoubleClick}
      >
        <BoardConflictsProvider conflicts={conflicts}>
        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <Card className="mx-auto mt-8 max-w-md">
            <EmptyState
              icon={AlertTriangle}
              tone="red"
              title="Couldn't load the board"
              hint={(error as Error)?.message ?? "Unknown error"}
            />
          </Card>
        ) : filteredByMode.length === 0 && (!activeTechs || activeTechs.length === 0) ? (
          <Card className="mx-auto mt-8 max-w-md">
            <EmptyState
              icon={Calendar}
              title="No dispatches on this date"
              hint="Either nothing's scheduled, or this is a weekend or holiday."
            />
          </Card>
        ) : filteredByMode.length === 0 && viewMode !== "active" ? (
          <Card className="mx-auto mt-8 max-w-md">
            <EmptyState
              icon={viewMode === "history" ? History : CheckCircle2}
              tone={viewMode === "history" ? "slate" : "emerald"}
              title={viewMode === "history" ? "Nothing completed yet" : "All dispatches completed"}
              hint={viewMode === "history" ? "Calls land here once techs mark them complete." : "Switch to History to see what's been finished."}
            />
          </Card>
        ) : layout === "cockpit" ? (
          <BoardCockpitView
            boardMode={boardMode}
            viewMode={viewMode}
            rows={filteredByMode}
            date={date}
            activeTechs={activeTechs}
            techFilter={techFilter}
            selected={selected}
            selectedTicket={selectedTicket}
            onSelect={selectDispatch}
            onSelectTicket={selectTicket}
            onToggleTech={toggleTech}
            availByTech={availByTech}
            onAddAvailability={openAddAvailability}
            onEditAvailability={openEditAvailability}
            onRebuildDay={setRebuildFor}
            density={density}
            showMap={mapMode === "docked"}
            pendingOptimize={pendingOptimize}
            onOptimizeConsumed={() => setPendingOptimize(false)}
          />
        ) : (
          <BoardBody
            boardMode={boardMode}
            viewMode={viewMode}
            rows={filteredByMode}
            date={date}
            activeTechs={activeTechs}
            techFilter={techFilter}
            onSelect={selectDispatch}
            onSelectTicket={selectTicket}
            availByTech={availByTech}
            onAddAvailability={openAddAvailability}
            onEditAvailability={openEditAvailability}
            onRebuildDay={setRebuildFor}
            density={density}
          />
        )}
        </BoardConflictsProvider>
      </div>

      {availModal && (
        <AvailabilityModal
          defaultEmpno={availModal.empno}
          date={date}
          techs={activeTechs ?? []}
          editEvent={availModal.edit}
          onClose={() => setAvailModal(null)}
          onAdded={onAvailabilityAdded}
        />
      )}

      {rebuildFor && (
        <RebuildDayModal
          outEmpno={rebuildFor}
          date={date}
          rows={filteredByMode}
          techs={activeTechs ?? []}
          onClose={() => setRebuildFor(null)}
        />
      )}

      {/* Off (columns) pops the modal on select; the cockpit — map docked OR
          in its own window (COCKPIT-WINDOW-01) — shows the same detail inline
          in its bottom strip, so suppress the modal there. */}
      <DispatchDetailModal
        dispatch={layout === "cockpit" ? null : selected}
        onClose={() => setSelected(null)}
      />
      {/* Pegasus-native jobs: single-click shows the cockpit strip (or the modal
          on columns/map); double-click / right-click "Open dispatch" pop THIS
          window on every layout via windowTicket (Seth 2026-06-24/25). */}
      <PegasusJobDetailModal
        job={windowTicket ?? (layout === "cockpit" ? null : selectedTicket)}
        onClose={() => {
          setWindowTicket(null);
          setSelectedTicket(null);
        }}
      />
      {/* BOARD-DBLCLICK-NEW: customer search → CreateDispatchModal with the
          double-clicked lane's tech + the board day prefilled. */}
      {newDispatch && (
        <NewDispatchLauncher
          empno={newDispatch.empno}
          techName={newDispatch.name}
          date={newDispatch.date ?? date}
          onClose={() => setNewDispatch(null)}
        />
      )}
    </div>
  );
}

function BoardBody({
  boardMode = "service",
  viewMode = "active",
  rows,
  date,
  activeTechs,
  techFilter,
  onSelect,
  onSelectTicket,
  availByTech,
  onAddAvailability,
  onEditAvailability,
  onRebuildDay,
  density = "cards",
  fitWidth = false,
  onFilterTech,
}: {
  boardMode?: BoardMode;
  viewMode?: ViewMode;
  rows: Dispatch[];
  date: string;
  activeTechs: ReturnType<typeof useActiveTechs>["data"];
  techFilter: string | null;
  onSelect: (d: Dispatch) => void;
  onSelectTicket?: (job: PendingTicket) => void;
  availByTech?: Map<string, { out: AvailEvent | null; timed: AvailEvent[] }>;
  onAddAvailability?: (empno: string) => void;
  onEditAvailability?: (e: AvailEvent) => void;
  onRebuildDay?: (empno: string) => void;
  density?: Density;
  /**
   * Cockpit fit-to-width (COCKPIT-FIT): share the available width across ALL
   * techs so the whole roster sits on ONE row — no horizontal scroll. Columns
   * become flexible (minmax(0,1fr)) instead of floored, so they compress to fit
   * the cockpit's narrower columns pane. The wide Columns layout leaves this off
   * and keeps the readable floor + horizontal scroll.
   */
  fitWidth?: boolean;
  /**
   * Cockpit only: the GLOBAL tech filter (same callback the map legend chip
   * uses). When provided + fitWidth, clicking a column's name header filters
   * the board AND the map to that tech (Seth: "clicking a tech's name should
   * reflect just their calls on the map, like the chip"), instead of the
   * board-scanning expand-in-place used in the wide Columns view.
   */
  onFilterTech?: (empno: string) => void;
}) {
  // Expand-IN-PLACE (Seth: "expand the selected tech's calls but leave the
  // other techs visible — I hate the reload"): clicking a tech's name widens
  // THAT column to rich cards while the rest of the roster stays put. Local
  // state only — no URL change, no lanes unmounting, no entrance re-animation.
  // (The map-chip techFilter still narrows to one lane — that's the cockpit's
  // working flow; this is the board-scanning flow.)
  const [expanded, setExpanded] = useState<string | null>(null);
  // Live drag preview shared by every lane in this body — which column + before
  // which item the dragged card will land (the drop indicator).
  const [dragPreview, setDragPreview] = useState<{ empno: string; beforeKey: string | null } | null>(null);
  // The "set window to X?" prompt lives at board level (keyed by job id) so it
  // SURVIVES a cross-tech move — the card remounts in the new lane and reads it
  // (Seth 2026-06-24: chip never showed when moving between techs). Auto-dismiss.
  const [windowPrompt, setWindowPrompt] = useState<{ jobId: string; window: string } | null>(null);
  const windowPromptTimer = useRef<number>(0);
  const promptWindow = useCallback((p: { jobId: string; window: string } | null) => {
    setWindowPrompt(p);
    window.clearTimeout(windowPromptTimer.current);
    if (p) windowPromptTimer.current = window.setTimeout(() => setWindowPrompt(null), 7000);
  }, []);
  const expandedTech = expanded ?? techFilter;
  const lanes = groupByTech(rows, activeTechs ?? null);
  // SCHED-02: Pegasus-scheduled jobs for this date, grouped into the tech lanes.
  const pegasusJobs = (useScheduledForBoard(date).data ?? []).filter(
    (j) => workTypeOf(j) === boardMode && pegasusVisibleForView(j, viewMode),
  );
  const pegasusByTech = new Map<string, PendingTicket[]>();
  for (const j of pegasusJobs) {
    if (!j.assigned_empno) continue;
    pegasusByTech.set(j.assigned_empno, [
      ...(pegasusByTech.get(j.assigned_empno) ?? []),
      j,
    ]);
  }
  // COCKPIT-FOCUS: in the cockpit (fitWidth) a tech-click focuses the MAP, not
  // the columns — keep EVERY lane visible (no collapse, no width reflow), just
  // highlight the focused one (the `expanded` ring, since expandedTech===techFilter
  // here). The wide Columns view still collapses to the filtered tech.
  const visible =
    techFilter && !fitWidth
      ? lanes.filter((l) => l.empno === techFilter)
      : lanes;

  if (visible.length === 0) {
    return (
      <Card className="mx-auto max-w-md p-10 text-center">
        <h3 className="text-sm font-medium text-slate-900">
          No matching tech on this date
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          The filtered tech has no stops today.
        </p>
      </Card>
    );
  }

  return (
    // Responsive-fit lanes (BOARD-RESPONSIVE-01): columns share the viewport
    // width and shrink as techs are added, capped at 340px so a few techs
    // don't stretch into oversized columns. FLOOR at 168px (AUDIT C2): with a
    // full roster, minmax(0,…) compressed lanes to ~110px — the tech's NAME
    // and the customer names truncated to nothing, leaving an anonymous board.
    // Below the floor the pane scrolls horizontally (parent is overflow-x-auto)
    // — a readable scrolling board beats an unreadable fitted one.
    <div
      className={cn("grid pb-4", density === "compact" ? "gap-2" : "gap-3")}
      style={{
        // Per-column widths: the expanded tech gets card-sized room, the rest
        // stay at their density's floor (compact blocks stay legible far
        // narrower — the point is the WHOLE roster on one screen).
        // COCKPIT-FIT: in fit-to-width mode the columns are flexible (no floor)
        // so the whole roster shares the pane on ONE row — never a side-scroll.
        gridTemplateColumns: visible
          .map((l) =>
            fitWidth
              ? // COCKPIT-FOCUS: every lane stays equal width — focusing a tech
                // highlights (ring) but never widens, so nothing reflows.
                "minmax(0, 1fr)"
              : l.empno === expandedTech
                ? "minmax(300px, 400px)"
                : density === "compact"
                  ? "minmax(118px, 340px)"
                  : "minmax(168px, 340px)",
          )
          .join(" "),
        // Cap the fitted grid so a 2-tech day doesn't stretch lanes absurdly
        // wide; with a full roster the cap exceeds the pane so lanes shrink.
        maxWidth: fitWidth ? `${visible.length * 360}px` : undefined,
      }}
    >
      {visible.map((lane, i) => (
        <TechColumn
          key={lane.empno}
          lane={lane}
          index={i}
          onSelect={onSelect}
          events={availByTech?.get(lane.empno)}
          onAddAvailability={onAddAvailability}
          onEditAvailability={onEditAvailability}
          onRebuildDay={onRebuildDay}
          pegasusJobs={pegasusByTech.get(lane.empno)}
          onSelectTicket={onSelectTicket}
          dragPreview={dragPreview}
          onDragPreview={setDragPreview}
          windowPrompt={windowPrompt}
          onWindowPrompt={promptWindow}
          density={lane.empno === expandedTech ? "cards" : density}
          expanded={lane.empno === expandedTech}
          firstNameOnly={fitWidth}
          boardDate={date}
          onToggleTech={
            // Cockpit (fitWidth): the name header filters the board + the map to
            // that tech — the same global filter the map legend chip uses (Seth).
            // Wide Columns view: keep the local board-scanning expand-in-place.
            fitWidth && onFilterTech
              ? onFilterTech
              : (empno) =>
                  setExpanded((cur) => (cur === empno ? null : empno))
          }
        />
      ))}
    </div>
  );
}

/**
 * Map layout for the Board (BOARD-MAP-01) — the day's customer stops plotted on
 * a Google map, colored by tech, with a clickable legend that filters to one
 * tech and pins that open the same detail modal as the columns. Pseudo stops
 * (Off Today / Parts / Office) and stops without coordinates are excluded from
 * the map; the un-geocoded count is surfaced so nothing silently disappears.
 *
 * This is the same `DispatchMap` the SEG-01 route leg will reuse.
 */
// Arrival-window → its clock start, so a Pegasus job with only a window (no
// exact time) still sorts into the day's sequence at a sane spot.
const WINDOW_START: Record<string, string> = {
  "7-8": "07:00", // Sedgwick install windows
  "8AM": "08:00",
  "8-12": "08:00",
  "10-2": "10:00",
  "11-1": "11:00",
  "12-4": "12:00",
  "3-7": "15:00",
};
/** Sortable "HH:MM" for a Pegasus job: exact time if set, else its window start. */
function pegSortTime(j: PendingTicket): string {
  if (j.scheduled_time) return j.scheduled_time.slice(0, 5);
  return (j.scheduled_window && WINDOW_START[j.scheduled_window]) || "~";
}

/**
 * Time band for a Pegasus job — mirrors `timeBand(Dispatch)` so the optimizer
 * keeps Pegasus stops in the same window order as ESC stops (morning band before
 * afternoon, after-hours last). From the window, else the exact-time hour.
 */
function pegBand(j: PendingTicket): number {
  const win = (j.scheduled_window ?? "").toUpperCase().trim();
  if (win.includes("AFTER")) return 100;
  const w = /^(\d{1,2})/.exec(win);
  if (w) {
    let h = parseInt(w[1], 10);
    if (h >= 1 && h <= 7) h += 12; // afternoon windows (1-7 → 13-19)
    return h;
  }
  const t = /^(\d{2}):/.exec(j.scheduled_time ?? "");
  if (t) return parseInt(t[1], 10);
  return 50;
}

/**
 * ROUTE-UNIFY — one stop on the route, whether it came from ESC (read-only) or a
 * Pegasus-native dispatch. Everything the route machinery does (number, optimize,
 * draw the line, drive-time, drag-reorder, cross-tech move) runs off this shape,
 * so a Pegasus call plans EXACTLY like an ESC call. `esc`/`peg` carry the source
 * row back for the right detail-open + the right write (ESC = dispatch_assignment
 * override; Pegasus = pegasus_dispatch.assigned_empno via useMoveDispatch).
 */
interface RouteStop {
  id: string; // ESC dispatch number, or `p-${uuid}` for a Pegasus job
  kind: "esc" | "pegasus";
  empno: string; // assigned tech
  lat: number;
  lng: number;
  t: string; // "HH:MM" sort key (planned order + drive sequencing)
  band: number; // time band for the banded optimizer
  active: boolean; // tech is currently here (ESC status); Pegasus never yet
  name: string; // display customer name
  city: string | null;
  clock: string | null; // formatted arrival time for the row
  window: string | null; // arrival-window label (row chip)
  priority: string | null; // ASAP etc. (row chip)
  esc?: Dispatch;
  peg?: PendingTicket;
}

// OPTIMIZE-DAY — call-type codes that ANCHOR a call to its tech (it may be
// re-ordered within that tech's day but never handed to another tech by the
// optimizer). Seth 2026-06-24: only plain windowed service calls, LONG, and
// QUOTE float; everything else stays put unless the tech is OUT (Rebuild Day).
const ANCHOR_CODES = new Set([
  "PART",
  "TECH",
  "PAIR",
  "VISIT2",
  "INSTAL",
  "REBATE",
  "PLUMB",
  "AFTER",
  "SCHED",
]);
const CODE_LABEL: Record<string, string> = {
  PART: "Part call",
  TECH: "Tech-specific",
  PAIR: "2-tech",
  VISIT2: "Return visit",
  INSTAL: "Install",
  REBATE: "Rebate",
  PLUMB: "Plumbing",
  AFTER: "After-hours",
  SCHED: "To schedule",
};
/**
 * Why a stop is locked to its tech for the whole-day optimizer, or null if it's
 * free to relocate. Order matters — status beats code beats a requested tech.
 */
function lockReasonForStop(s: RouteStop, requestedEmpno?: string | null): string | null {
  const status = s.esc?.status ?? "";
  if (STATUS_DONE.has(status)) return "Completed";
  if (s.active) return "En route";
  const code = (s.priority ?? "").toUpperCase();
  if (code && ANCHOR_CODES.has(code)) return CODE_LABEL[code] ?? code;
  if (requestedEmpno) return "Requested tech";
  return null;
}

/**
 * Build a `RecoStop` (recommendation-engine + MovePreview shape) from a Pegasus
 * job, so Pegasus stops fold into the SAME cross-tech move + skill machinery ESC
 * uses. `roster`/`rosterMap` are the caller's tech-name maps (built once per memo).
 */
function pegRecoStop(
  j: PendingTicket,
  roster: { empno: string; first: string | null | undefined }[],
  rosterMap: Map<string, string | null | undefined>,
): RecoStop {
  const notes = j.notes ?? "";
  const excludedEmpnos = deriveExcludedTechs(notes, roster);
  let reqEmpno = deriveRequestedTech(notes, roster);
  if (reqEmpno && excludedEmpnos.includes(reqEmpno)) reqEmpno = null;
  return {
    dispatch: `p-${j.id}`,
    lat: j.lat as number,
    lng: j.lng as number,
    label: j.customer_name,
    city: j.city ?? null,
    band: pegBand(j),
    requiredSkills: deriveRequiredSkills(j.notes),
    requestedEmpno: reqEmpno,
    requestedName: reqEmpno ? rosterMap.get(reqEmpno) ?? null : null,
    requestedEmpnos: deriveRequestedTechs(notes, roster).filter((e) => !excludedEmpnos.includes(e)),
    excludedEmpnos,
    attrLock: deriveAttrLock(notes),
    clock: j.scheduled_time ? formatTime(j.scheduled_time) : null,
    win: j.scheduled_window ?? null,
  };
}

export function BoardMapView({
  boardMode = "service",
  viewMode = "active",
  rows,
  date,
  techFilter,
  selected,
  selectedTicket,
  onSelect,
  onSelectTicket,
  onToggleTech,
  compact = false,
  pendingOptimize = false,
  onOptimizeConsumed,
}: {
  boardMode?: BoardMode;
  viewMode?: ViewMode;
  rows: Dispatch[];
  date: string;
  techFilter: string | null;
  selected: Dispatch | null;
  selectedTicket: PendingTicket | null;
  onSelect: (d: Dispatch | null) => void;
  onSelectTicket: (t: PendingTicket | null) => void;
  onToggleTech: (empno: string) => void;
  compact?: boolean;
  /** Launched from the Columns toolbar → auto-open the whole-day optimizer. */
  pendingOptimize?: boolean;
  onOptimizeConsumed?: () => void;
}) {
  const hasCoord = (r: Dispatch) =>
    usableCoords(r.loc_latitude, r.loc_longitude);

  // Real (non-pseudo) customer stops for the day.
  const baseRows = rows.filter((r) => !isPseudoDispatch(r));

  // Pegasus-native scheduled jobs for the day (BOARD-MAP-PEGASUS) — these show
  // in the columns but were missing from the map. Plot them as pins too, colored
  // by their assigned tech, clickable into the Pegasus detail. ESC stays read-only.
  const pegasusJobs = (useScheduledForBoard(date).data ?? []).filter(
    (j) => workTypeOf(j) === boardMode && pegasusVisibleForView(j, viewMode),
  );
  const pegHasCoord = (j: PendingTicket) => usableCoords(j.lat, j.lng);
  const pegByCoord = pegasusJobs.filter(pegHasCoord); // all techs (legend + colors)
  const pegVisible = techFilter
    ? pegasusJobs.filter((j) => j.assigned_empno === techFilter)
    : pegasusJobs;
  const pegMappable = pegVisible.filter(pegHasCoord);
  const pegUnmapped = pegVisible.length - pegMappable.length;

  // Stable per-tech color across the whole day (independent of the filter) —
  // ESC stops AND Pegasus jobs share one palette so a tech's pins all match.
  const techEmpnos = [
    ...new Set([
      ...baseRows.map((r) => r.tech_empno),
      ...pegByCoord.map((j) => j.assigned_empno).filter((e): e is string => !!e),
    ]),
  ].sort();
  const techColor = new Map<string, string>();
  techEmpnos.forEach((e, i) =>
    techColor.set(e, MAP_PALETTE[i % MAP_PALETTE.length]),
  );

  // Authoritative tech roster (by empno) — used for names so a call that's been
  // moved to another tech doesn't show its OLD tech's name (the row carries the
  // old tech_name; the roster is keyed by the real empno).
  const rosterTechs = useActiveTechs(DEPT_FOR_MODE[boardMode]).data;
  const nameFor = (empno: string) => {
    const t = rosterTechs?.find((x) => x.empno === empno);
    if (t) {
      const rn = t.empname ?? `${t.firstname ?? ""} ${t.lastname ?? ""}`.trim();
      if (rn && rn.length) return rn;
    }
    const r = baseRows.find((x) => x.tech_empno === empno);
    const n =
      r?.tech_name ?? `${r?.tech_first ?? ""} ${r?.tech_last ?? ""}`.trim();
    return n && n.length ? n : empno;
  };

  // Located availability events → map WAYPOINTS (TECH-AVAIL-02): a meeting or
  // errand whose Location was picked from Places carries coordinates, so the
  // dispatcher sees WHERE the tech goes — and, when filtered to that tech, the
  // route path drives through it. Group meetings collapse to one diamond pin.
  const availEvents = useTechAvailability(date).data ?? [];
  const fmtClock = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  const waypointGroups = new Map<string, AvailEvent[]>();
  for (const e of availEvents) {
    if (e.location_lat == null || e.location_lng == null) continue;
    if (techFilter && e.empno !== techFilter) continue;
    const k = e.group_id ?? e.id;
    waypointGroups.set(k, [...(waypointGroups.get(k) ?? []), e]);
  }

  // Legend: techs with at least one mappable stop today + their day count.
  const legend = techEmpnos
    .map((e) => ({
      empno: e,
      name: nameFor(e),
      color: techColor.get(e) ?? "#2563eb",
      count:
        baseRows.filter((r) => r.tech_empno === e && hasCoord(r)).length +
        pegByCoord.filter((j) => j.assigned_empno === e).length,
    }))
    .filter((t) => t.count > 0);

  // Recommendation engine inputs (all techs' stops + skills) — memoized on a
  // stable signature so it (and the route math it runs) doesn't recompute on
  // unrelated re-renders.
  const techSkillsData = useTechSkills().data;
  const seniority = useTechSeniority().data;
  const locationNotes = useLocationNotes(baseRows.map((r) => r.custno)).data;
  const baseRowsSig = baseRows
    .map((r) => `${r.dispatch}:${r.tech_empno}`)
    .join(",");
  const locNotesSig = Object.keys(locationNotes ?? {}).sort().join(",");
  const rosterSig = (rosterTechs ?? []).map((t) => t.empno).join(",");
  // Change-signal for the Pegasus stops folded into recoTechs below.
  const pegSig = pegByCoord
    .map((j) => `p-${j.id}:${j.assigned_empno}`)
    .join(",");
  const recoTechs = useMemo<RecoTech[]>(() => {
    // Tech first-name roster (for matching customer-stated tech requests). Built
    // from the FULL active roster (+ anyone with stops) so a requested tech is
    // recognizable even on a day they have no calls of their own.
    const rosterMap = new Map<string, string | null | undefined>();
    for (const t of rosterTechs ?? []) rosterMap.set(t.empno, t.firstname);
    for (const r of baseRows)
      if (!rosterMap.has(r.tech_empno)) rosterMap.set(r.tech_empno, r.tech_first);
    const roster = [...rosterMap].map(([empno, first]) => ({ empno, first }));
    const byTech = new Map<string, RecoTech["stops"]>();
    for (const r of baseRows) {
      if (!usableCoords(r.loc_latitude, r.loc_longitude)) continue;
      const locNote = locationNotes?.[`${r.custno}|${r.locno}`] ?? "";
      const combinedNotes = `${r.notes ?? ""}\n${locNote}`;
      const excludedEmpnos = deriveExcludedTechs(combinedNotes, roster);
      let reqEmpno = deriveRequestedTech(combinedNotes, roster);
      // A tech the notes explicitly EXCLUDE can never be the "requested" tech —
      // belt-and-suspenders for "Do not send Camron" being read as a request.
      if (reqEmpno && excludedEmpnos.includes(reqEmpno)) reqEmpno = null;
      const arr = byTech.get(r.tech_empno) ?? [];
      arr.push({
        dispatch: r.dispatch,
        // usableCoords() above guarantees these are numbers (TS can't narrow
        // across a two-arg predicate, hence the assertions).
        lat: r.loc_latitude!,
        lng: r.loc_longitude!,
        label: displayCustomerName(r.locname, r.customer_fullname),
        city: r.city,
        band: timeBand(r),
        requiredSkills: deriveRequiredSkills(r.notes),
        requestedEmpno: reqEmpno,
        requestedName: reqEmpno ? rosterMap.get(reqEmpno) ?? null : null,
        requestedEmpnos: deriveRequestedTechs(combinedNotes, roster).filter((e) => !excludedEmpnos.includes(e)),
        excludedEmpnos,
        attrLock: deriveAttrLock(combinedNotes),
      });
      byTech.set(r.tech_empno, arr);
    }
    // Pegasus-native jobs join the SAME per-tech route inputs so the optimizer,
    // mileage, skill flags + cross-tech move all treat them as real stops. (ROUTE-UNIFY)
    for (const j of pegByCoord) {
      if (!j.assigned_empno) continue;
      const arr = byTech.get(j.assigned_empno) ?? [];
      arr.push(pegRecoStop(j, roster, rosterMap));
      byTech.set(j.assigned_empno, arr);
    }
    return [...byTech.entries()].map(([empno, stops]) => ({
      empno,
      name: nameFor(empno),
      skills: new Set(techSkillsData?.[empno] ?? []),
      stops,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRowsSig, techSkillsData, locNotesSig, rosterSig, pegSig]);
  // Gate on skills being loaded — computing before they arrive would show false
  // "not qualified" flags (every tech looks skill-less).
  const recommendations = useMemo(
    () =>
      techFilter && techSkillsData
        ? recommendForTech(techFilter, recoTechs)
        : [],
    [techFilter, recoTechs, techSkillsData],
  );
  // Recommendation stops keyed by id — for the whole-day optimizer's skill /
  // requested-tech / excluded-tech inputs. (OPTIMIZE-DAY-1)
  const recoStopById = useMemo(() => {
    const m = new Map<string, RecoStop>();
    for (const t of recoTechs) for (const s of t.stops) m.set(s.dispatch, s);
    return m;
  }, [recoTechs]);

  // Points actually plotted = (filtered to one tech if active) ∩ has-coords.
  const visibleRows = techFilter
    ? baseRows.filter((r) => r.tech_empno === techFilter)
    : baseRows;
  const mappable = visibleRows.filter(hasCoord);

  // ROUTE-UNIFY — the single list the route machinery runs on: ESC stops AND
  // Pegasus-native jobs, normalized to one shape, techFilter-applied. Everything
  // below (numbering, optimizer, route line, drive-time, the drag/pin editor,
  // cross-tech move) reads `routeStops`/`stopById` so a Pegasus call plans like
  // an ESC call. `rowById` stays for the ESC-only pin path.
  const rowById = new Map(mappable.map((r) => [r.dispatch, r] as const));
  const escStops: RouteStop[] = mappable.map((r) => {
    const { timeWindow, priority } = resolveWindowAndPriority(
      r.window_code,
      r.notes,
    );
    return {
      id: r.dispatch,
      kind: "esc",
      empno: r.tech_empno,
      lat: r.loc_latitude as number,
      lng: r.loc_longitude as number,
      t: (r.tpromtime ?? "~").slice(0, 5) || "~",
      band: timeBand(r),
      active: STATUS_ACTIVE.has(r.status ?? ""),
      name: displayCustomerName(r.locname, r.customer_fullname),
      city: r.city ?? null,
      clock: formatTime(r.tpromtime) || null,
      window: timeWindow,
      priority,
      esc: r,
    };
  });
  const pegStops: RouteStop[] = pegMappable.map((j) => ({
    id: `p-${j.id}`,
    kind: "pegasus",
    empno: j.assigned_empno ?? "",
    lat: j.lat as number,
    lng: j.lng as number,
    t: pegSortTime(j),
    band: pegBand(j),
    active: false,
    name: j.customer_name,
    city: j.city ?? null,
    clock: j.scheduled_time ? formatTime(j.scheduled_time) : null,
    window: j.scheduled_window ?? null,
    priority: j.urgency === "asap" ? "ASAP" : null,
    peg: j,
  }));
  const routeStops: RouteStop[] = [...escStops, ...pegStops];
  const stopById = new Map(routeStops.map((s) => [s.id, s] as const));
  const unmapped = visibleRows.length - mappable.length + pegUnmapped;

  // Whole-day optimizer (OPTIMIZE-DAY-1) — runs on demand over ALL techs' stops
  // (only offered when no tech filter is active). Builds the lock-aware inputs,
  // proposes a board-wide plan, and opens the read-only review modal. No writes.
  const [dayPlan, setDayPlan] = useState<{
    plan: WholeDayPlan;
    stops: OptiStop[];
  } | null>(null);
  const runWholeDayOptimize = () => {
    const optiStops: OptiStop[] = routeStops.map((s) => {
      const reco = recoStopById.get(s.id);
      const lockReason = lockReasonForStop(s, reco?.requestedEmpno);
      return {
        id: s.id,
        empno: s.empno,
        lat: s.lat,
        lng: s.lng,
        band: s.band,
        t: s.t,
        movable: lockReason === null,
        requiredSkills: reco?.requiredSkills ?? [],
        excludedEmpnos: reco?.excludedEmpnos ?? [],
        name: s.name,
        city: s.city,
        window: s.window,
        lockReason,
        note: s.esc
          ? extractJobPreview(s.esc.notes, 140)
          : s.peg?.notes ?? s.peg?.summary ?? null,
        asap: s.priority === "ASAP",
      };
    });
    const optiTechs = recoTechs.map((t) => ({
      empno: t.empno,
      skills: t.skills,
      // Real seniority: tenure ≥5yr (hire_date) OR ≥10 skills (PREF-FINDER); the
      // skills-count is a fallback only if hire_date hasn't loaded.
      senior: seniority?.get(t.empno)?.senior ?? t.skills.size >= 10,
    }));
    setDayPlan({ plan: planWholeDay(optiStops, optiTechs), stops: optiStops });
  };
  const [applyingDay, setApplyingDay] = useState(false);
  // After Apply: the moves we made (id + original tech) so Undo can revert them.
  const [undoApply, setUndoApply] = useState<
    { id: string; from: string }[] | null
  >(null);
  // Columns "Optimize day" launcher: auto-open the optimizer once the flag arrives
  // (we've switched to the map) and there's something to optimize.
  useEffect(() => {
    if (!pendingOptimize) return;
    if (!dayPlan && routeStops.length >= 2) runWholeDayOptimize();
    onOptimizeConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOptimize, routeStops.length]);

  // Planned (scheduled-time) order per tech — ESC + Pegasus interleaved by time,
  // so the sequence numbers reflect the real shape of the day.
  const orderById = new Map<string, number>();
  {
    const byTech = new Map<string, RouteStop[]>();
    for (const s of routeStops) {
      if (!s.empno) continue;
      if (!byTech.has(s.empno)) byTech.set(s.empno, []);
      byTech.get(s.empno)!.push(s);
    }
    for (const list of byTech.values()) {
      list.sort((a, b) => a.t.localeCompare(b.t));
      list.forEach((s, i) => orderById.set(s.id, i + 1));
    }
  }
  const plannedIds = [...routeStops]
    .sort((a, b) => (orderById.get(a.id) ?? 0) - (orderById.get(b.id) ?? 0))
    .map((s) => s.id);

  // Window-aware optimized order (ROUTE-04a) — only when filtered to one tech.
  // Memoized so the 2-opt math doesn't re-run on unrelated re-renders. Now over
  // the UNIFIED stops, so a Pegasus call is optimized into the run like any other.
  const optimizeSig = `${techFilter}|${routeStops.map((s) => s.id).join(",")}`;
  const { optimizedIds, savedMiles, savedMin } = useMemo(() => {
    if (!techFilter || routeStops.length < 2)
      return { optimizedIds: [] as string[], savedMiles: 0, savedMin: 0 };
    const coords = routeStops.map((s) => ({
      id: s.id,
      lat: s.lat,
      lng: s.lng,
      t: s.t,
      band: s.band,
    }));
    // Anchor at the tech's current position (the active stop) so a mid-day
    // re-optimize plans from where he is now (approx until live GPS).
    const activeStop = routeStops.find((s) => s.active);
    const start = activeStop
      ? { lat: activeStop.lat, lng: activeStop.lng }
      : undefined;
    const plannedSeq = [...coords].sort((a, b) => a.t.localeCompare(b.t));
    const optimizedSeq = optimizeBanded(coords, start);
    const sm = Math.max(0, routeMiles(plannedSeq) - routeMiles(optimizedSeq));
    return {
      optimizedIds: optimizedSeq.map((p) => p.id),
      savedMiles: sm,
      savedMin: driveMinutes(sm),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizeSig]);

  // BUILD-20a: the dispatcher's working/saved stop order for this tech + day.
  const { data: savedPlan } = useRoutePlan(date, techFilter);
  const savePlan = useSaveRoutePlan();
  const setAssignment = useSetAssignment();
  const moveDispatch = useMoveDispatch(); // Pegasus cross-tech move (assigned_empno)

  // Commit a whole-day optimize (OPTIMIZE-DAY-2): write the kept reassignments
  // (ESC override / Pegasus assigned_empno) + each tech's optimized run order,
  // then remember what moved so the dispatcher can Undo. ESC stays read-only.
  const applyWholeDay = async (commit: WholeDayCommit) => {
    setApplyingDay(true);
    try {
      for (const r of commit.reassignments) {
        if (r.id.startsWith("p-"))
          await moveDispatch.mutateAsync({ id: r.id.slice(2), empno: r.to });
        else await setAssignment.mutateAsync({ dispatch: r.id, tech: r.to });
      }
      for (const rp of commit.routePlans) {
        await savePlan.mutateAsync({
          date,
          tech: rp.tech,
          sequence: rp.order.map((d) => ({ d, pin: false })),
        });
      }
      setUndoApply(
        commit.reassignments.map((r) => ({ id: r.id, from: r.from })),
      );
      setDayPlan(null);
    } finally {
      setApplyingDay(false);
    }
  };
  const undoWholeDay = async () => {
    if (!undoApply) return;
    setApplyingDay(true);
    try {
      for (const m of undoApply) {
        if (m.id.startsWith("p-"))
          await moveDispatch.mutateAsync({ id: m.id.slice(2), empno: m.from });
        else await setAssignment.mutateAsync({ dispatch: m.id, tech: m.from });
      }
      setUndoApply(null);
    } finally {
      setApplyingDay(false);
    }
  };

  const [workingOrder, setWorkingOrder] = useState<string[] | null>(null);
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  const [moveFor, setMoveFor] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const initSigRef = useRef("");
  const mapSig = routeStops
    .map((s) => s.id)
    .sort()
    .join(",");

  useEffect(() => {
    if (!techFilter || routeStops.length === 0) {
      setWorkingOrder(null);
      setPinned(new Set());
      initSigRef.current = "";
      return;
    }
    const sig = `${date}|${techFilter}|${mapSig}|${
      savedPlan ? "s" + savedPlan.length : "n"
    }`;
    if (sig === initSigRef.current) return;
    initSigRef.current = sig;
    if (savedPlan && savedPlan.length) {
      const idset = new Set(routeStops.map((s) => s.id));
      const ordered = savedPlan.map((s) => s.d).filter((d) => idset.has(d));
      const inPlan = new Set(ordered);
      const extras = plannedIds.filter((d) => !inPlan.has(d));
      setWorkingOrder([...ordered, ...extras]);
      setPinned(
        new Set(savedPlan.filter((s) => s.pin).map((s) => s.d)),
      );
    } else {
      setWorkingOrder(plannedIds);
      setPinned(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [techFilter, date, mapSig, savedPlan]);

  const orderIndex = new Map<string, number>();
  if (workingOrder) workingOrder.forEach((d, i) => orderIndex.set(d, i + 1));
  const orderFor = (id: string) =>
    workingOrder ? orderIndex.get(id) : orderById.get(id);

  const hasSaved = !!savedPlan;
  const currentSeq = workingOrder
    ? workingOrder.map((d) => ({ d, pin: pinned.has(d) }))
    : [];
  const changedFromSaved =
    hasSaved && JSON.stringify(currentSeq) !== JSON.stringify(savedPlan);
  const canSave = !!workingOrder && (!hasSaved || changedFromSaved);
  const matchesOptimized =
    !!workingOrder &&
    optimizedIds.length > 0 &&
    JSON.stringify(workingOrder) === JSON.stringify(optimizedIds);
  const matchesPlanned =
    !!workingOrder &&
    JSON.stringify(workingOrder) === JSON.stringify(plannedIds);

  const moveStop = (id: string, dir: -1 | 1) =>
    setWorkingOrder((cur) => {
      if (!cur) return cur;
      const i = cur.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= cur.length) return cur;
      const next = cur.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  // Drag-to-reorder: move `fromId` to `toId`'s slot.
  const reorderTo = (fromId: string, toId: string) =>
    setWorkingOrder((cur) => {
      if (!cur || fromId === toId) return cur;
      const from = cur.indexOf(fromId);
      const to = cur.indexOf(toId);
      if (from < 0 || to < 0) return cur;
      const next = cur.slice();
      next.splice(from, 1);
      next.splice(to, 0, fromId);
      return next;
    });
  // Smooth pointer drag: grab the grip, the list reflows live under the pointer
  // (the dragged row is pointer-events-none so hit-testing finds the row below).
  const startDrag = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    dragIdRef.current = id;
    setDragId(id);
    try {
      listRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // capture unsupported / pointer not active — move/up still work
    }
  };
  const onDragMove = (e: React.PointerEvent) => {
    const id = dragIdRef.current;
    if (!id) return;
    const row = document
      .elementFromPoint(e.clientX, e.clientY)
      ?.closest("[data-stopid]");
    const overD = row?.getAttribute("data-stopid");
    if (overD && overD !== id) reorderTo(id, overD);
  };
  const endDrag = (e: React.PointerEvent) => {
    dragIdRef.current = null;
    try {
      if (listRef.current?.hasPointerCapture(e.pointerId)) {
        listRef.current.releasePointerCapture(e.pointerId);
      }
    } catch {
      // ignore
    }
    setDragId(null);
  };
  const applyPlanned = () =>
    workingOrder &&
    setWorkingOrder(applyOrderWithPins(workingOrder, plannedIds, pinned));
  const applyOptimized = () =>
    optimizedIds.length > 0 &&
    workingOrder &&
    setWorkingOrder(applyOrderWithPins(workingOrder, optimizedIds, pinned));
  const togglePin = (id: string) =>
    setPinned((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const doSave = () => {
    if (!techFilter || !workingOrder) return;
    savePlan.mutate({
      date,
      tech: techFilter,
      sequence: workingOrder.map((d) => ({ d, pin: pinned.has(d) })),
    });
  };

  // Cross-tech move (BUILD-20b): for a stop, the cost of adding it to each OTHER
  // tech's route (best-insertion), cheapest first. Each candidate tech's existing
  // route counts BOTH its ESC stops and its Pegasus jobs, so the insertion math is
  // honest about how full the tech's day really is. (ROUTE-UNIFY)
  const moveCandidates = (stop: RouteStop) => {
    const sp = { lat: stop.lat, lng: stop.lng };
    return legend
      .filter((t) => t.empno !== techFilter)
      .map((t) => {
        const escForTech = baseRows
          .filter((r) => r.tech_empno === t.empno && hasCoord(r))
          .map((r) => ({
            lat: r.loc_latitude as number,
            lng: r.loc_longitude as number,
            t: (r.tpromtime ?? "~").slice(0, 5) || "~",
          }));
        const pegForTech = pegByCoord
          .filter((j) => j.assigned_empno === t.empno)
          .map((j) => ({ lat: j.lat as number, lng: j.lng as number, t: pegSortTime(j) }));
        const tStops = [...escForTech, ...pegForTech]
          .sort((a, b) => a.t.localeCompare(b.t))
          .map(({ lat, lng }) => ({ lat, lng }));
        return {
          empno: t.empno,
          name: t.name,
          color: t.color,
          cost: insertionCostMiles(tStops, sp),
        };
      })
      .sort((a, b) => a.cost - b.cost);
  };

  const waypointPoints: MapPoint[] = [...waypointGroups.values()].map(
    (group) => {
      const e = group[0];
      const who = group.map((g) => nameFor(g.empno)).join(", ");
      const single = group.length === 1 ? group[0].empno : null;
      return {
        id: `avail:${e.group_id ?? e.id}`,
        lat: e.location_lat as number,
        lng: e.location_lng as number,
        label: e.title || AVAIL_KIND_LABEL[e.kind],
        sublabel: e.location ?? undefined,
        colorHex: (single ? techColor.get(single) : undefined) ?? "#475569",
        shape: "waypoint" as const,
        hover: {
          title: e.title || AVAIL_KIND_LABEL[e.kind],
          chips: [
            {
              text: AVAIL_KIND_LABEL[e.kind],
              className: "bg-violet-100 text-violet-700",
            },
          ],
          meta: e.all_day
            ? `All day · ${who}`
            : `${fmtClock(e.start_at)} – ${fmtClock(e.end_at)} · ${who}`,
          note: e.location ?? undefined,
        },
      };
    },
  );

  const stopPoints: MapPoint[] = mappable.map((r) => {
    const { timeWindow, priority } = resolveWindowAndPriority(
      r.window_code,
      r.notes,
    );
    const chips: { text: string; className?: string }[] = [];
    if (timeWindow) {
      const c = windowColorClasses(timeWindow);
      chips.push({ text: timeWindow, className: cn(c.bg, c.text) });
    }
    if (priority) {
      const c = windowColorClasses(priority);
      chips.push({ text: priority, className: cn(c.bg, c.text) });
    }
    const name = displayCustomerName(r.locname, r.customer_fullname);
    return {
      id: r.dispatch,
      lat: r.loc_latitude as number,
      lng: r.loc_longitude as number,
      label: name,
      sublabel:
        [r.window_code, r.city].filter(Boolean).join(" · ") || undefined,
      colorKey: r.tech_empno,
      order: orderFor(r.dispatch),
      hover: {
        title: name,
        chips,
        meta:
          [formatTime(r.tpromtime), r.city].filter(Boolean).join(" · ") ||
          undefined,
        note: extractJobPreview(r.notes, 90),
      },
    };
  });
  // Pegasus-native scheduled jobs as pins — same look as ESC stops, colored by
  // the assigned tech, so they stop disappearing from the map. (BOARD-MAP-PEGASUS)
  const pegasusPoints: MapPoint[] = pegMappable.map((j) => {
    const win = j.scheduled_window;
    const chips: { text: string; className?: string }[] = [];
    if (win) {
      const c = windowColorClasses(win);
      chips.push({ text: win, className: cn(c.bg, c.text) });
    }
    if (j.urgency === "asap")
      chips.push({ text: "ASAP", className: "bg-red-100 text-red-700" });
    const name = j.customer_name;
    return {
      id: `p-${j.id}`,
      lat: j.lat as number,
      lng: j.lng as number,
      label: name,
      sublabel: [win, j.city].filter(Boolean).join(" · ") || undefined,
      colorKey: j.assigned_empno ?? undefined,
      order: orderFor(`p-${j.id}`),
      hover: {
        title: name,
        chips,
        meta:
          [j.scheduled_time ? formatTime(j.scheduled_time) : win, j.city]
            .filter(Boolean)
            .join(" · ") || undefined,
        note: j.notes ? j.notes.slice(0, 90) : undefined,
      },
    };
  });
  const points: MapPoint[] = [
    ...stopPoints,
    ...pegasusPoints,
    ...waypointPoints,
  ];

  // Real driving distance + time for the tech's current order (Distance Matrix,
  // cached + on-demand). null when the API isn't enabled → the UI keeps the
  // straight-line estimate. (ROUTE-REAL-01)
  // The filtered tech's TIMED located events (dentist run, meeting) are slotted
  // into the sequence by clock time, so the road path — and the "Actual
  // driving" number — drive THROUGH the waypoint, not past it.
  const routedWaypoints = techFilter
    ? availEvents
        .filter(
          (e) =>
            e.empno === techFilter &&
            !e.all_day &&
            e.location_lat != null &&
            e.location_lng != null,
        )
        .sort((a, b) => a.start_at.localeCompare(b.start_at))
    : [];
  const workingLatLng = (() => {
    const seq = (workingOrder ?? [])
      .map((d) => stopById.get(d))
      .filter((s): s is RouteStop => !!s)
      .map((s) => {
        const hh = parseInt(s.t.slice(0, 2), 10);
        const mm = parseInt(s.t.slice(3, 5), 10);
        return {
          lat: s.lat,
          lng: s.lng,
          t: Number.isFinite(hh) ? hh * 60 + (Number.isFinite(mm) ? mm : 0) : -1,
        };
      });
    for (const e of routedWaypoints) {
      const d = new Date(e.start_at);
      const t = d.getHours() * 60 + d.getMinutes();
      // After every stop the tech runs before the event starts.
      let idx = seq.length;
      for (let i = 0; i < seq.length; i++) {
        if (seq[i].t > t) {
          idx = i;
          break;
        }
      }
      seq.splice(idx, 0, {
        lat: e.location_lat as number,
        lng: e.location_lng as number,
        t,
      });
    }
    return seq.map(({ lat, lng }) => ({ lat, lng }));
  })();
  const realDrive = useRouteDrive(workingLatLng).data;
  // The actual road-following path for the filtered tech's order (Routes API).
  const roadPath = useRoadPath(workingLatLng).data;

  return (
    <div className="flex h-full flex-col gap-3">
      {legend.length > 0 && (
        // TECH SELECTOR (COCKPIT-FIT): avatar chips, not bare color dots. The
        // tech's MAP color survives as a thin halo around the face so the dots
        // on the map still map to a person at a glance. Active tech = filled
        // brand chip; a leading "All techs" reset makes getting back obvious.
        <div className="flex flex-wrap items-center gap-1.5">
          {!techFilter && routeStops.length >= 2 && (
            <button
              type="button"
              onClick={runWholeDayOptimize}
              title="Propose a board-wide optimized plan — you review before anything changes"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-700"
            >
              <Sparkles className="h-3.5 w-3.5" /> Optimize whole day
            </button>
          )}
          {/* Back-to-everyone reset — only while one tech is in focus. */}
          {techFilter && (
            <button
              type="button"
              onClick={() => onToggleTech(techFilter)}
              title="Show all techs"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-e1 transition-colors hover:bg-slate-50"
            >
              <Users className="h-3.5 w-3.5" /> All techs
            </button>
          )}
          {legend.map((t) => {
            const active = techFilter === t.empno;
            const dim = techFilter !== null && !active;
            const first = t.name.split(/\s+/)[0];
            return (
              <button
                key={t.empno}
                type="button"
                onClick={() => onToggleTech(t.empno)}
                className={cn(
                  "group inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2.5 transition-all duration-150 ease-out-quart",
                  active
                    ? "border-brand-300 bg-brand-50 shadow-e1 ring-1 ring-brand-200"
                    : "border-hairline bg-white hover:border-slate-300 hover:bg-slate-50",
                  dim && "opacity-45 hover:opacity-100",
                )}
                title={
                  active
                    ? "Show all techs"
                    : `Show only ${t.name} — plan their route`
                }
              >
                {/* Colored halo ties the face to its dots on the map. */}
                <span
                  className="flex items-center justify-center rounded-full p-[2px]"
                  style={{ backgroundColor: t.color }}
                >
                  <TechAvatar
                    empno={t.empno}
                    name={t.name}
                    size="xs"
                    className="ring-[1.5px] ring-white"
                  />
                </span>
                <span
                  className={cn(
                    "text-xs font-semibold",
                    active ? "text-brand-700" : "text-slate-700",
                  )}
                >
                  {first}
                </span>
                <span
                  className={cn(
                    "min-w-[1.125rem] rounded-full px-1 py-px text-center text-[11px] font-semibold tabular-nums",
                    active
                      ? "bg-brand-600 text-white"
                      : "bg-slate-100 text-slate-500 group-hover:bg-slate-200",
                  )}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
          {unmapped > 0 && (
            <span className="text-xs text-slate-500">
              {unmapped} stop{unmapped === 1 ? "" : "s"} not yet mapped
            </span>
          )}
        </div>
      )}

      {dayPlan && (
        <WholeDayOptimizeModal
          plan={dayPlan.plan}
          stops={dayPlan.stops}
          techName={nameFor}
          recoTechs={recoTechs}
          rows={baseRows}
          onApply={applyWholeDay}
          applying={applyingDay}
          onClose={() => setDayPlan(null)}
        />
      )}

      {undoApply && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
          <span className="min-w-0 flex-1">
            Optimized plan applied —{" "}
            {undoApply.length === 0
              ? "routes reordered"
              : `${undoApply.length} call${undoApply.length === 1 ? "" : "s"} moved`}
            .
          </span>
          {undoApply.length > 0 && (
            <button
              type="button"
              onClick={undoWholeDay}
              disabled={applyingDay}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-300 bg-white px-2 py-0.5 font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50"
            >
              <RotateCcw className="h-3 w-3" /> Undo
            </button>
          )}
          <button
            type="button"
            onClick={() => setUndoApply(null)}
            title="Dismiss"
            className="shrink-0 rounded p-0.5 text-emerald-600 hover:bg-emerald-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="relative min-h-0 flex-1">
        {points.length === 0 ? (
          <Card className="flex h-full items-center justify-center p-10 text-center">
            <div>
              <h3 className="text-sm font-medium text-slate-900">
                Nothing to map here
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                {unmapped > 0
                  ? `${unmapped} stop${unmapped === 1 ? "" : "s"} on this day ${
                      unmapped === 1 ? "has" : "have"
                    } no coordinates yet.`
                  : "No customer stops on this date."}
              </p>
            </div>
          </Card>
        ) : (
          <DispatchMap
            points={points}
            selectedId={
              selected?.dispatch ??
              (selectedTicket ? `p-${selectedTicket.id}` : null)
            }
            onSelectPoint={(id) => {
              if (id.startsWith("p-")) {
                onSelectTicket(
                  pegasusJobs.find((p) => `p-${p.id}` === id) ?? null,
                );
              } else {
                onSelect(rowById.get(id) ?? null);
              }
            }}
            colorForKey={(k) => techColor.get(k) ?? "#2563eb"}
            showRoutes={Boolean(techFilter)}
            routePath={techFilter ? roadPath : null}
            viewStorageKey="pegasus.board.map.view"
          />
        )}

        {/* Route editor (BUILD-20a) — propose → reorder → remember. Full Map
            view only; the Cockpit gets a roomier "Pegasus recommends" strip
            below its map instead (this editor is too dense for the small pane). */}
        {!compact && techFilter && workingOrder && workingOrder.length >= 1 && (
          <div className="absolute left-2 top-2 z-10 flex max-h-[calc(100%-1rem)] w-72 flex-col overflow-hidden rounded-xl border border-hairline bg-white/95 shadow-e2 backdrop-blur">
            <div className="flex items-center justify-between gap-2 border-b border-hairline px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">
                  {nameFor(techFilter)}&apos;s route
                </div>
                <div className="text-[11px] text-slate-500">
                  {hasSaved
                    ? changedFromSaved
                      ? "Saved plan · edited"
                      : "Saved plan ✓"
                    : "Not saved yet"}
                </div>
              </div>
              <button
                type="button"
                onClick={doSave}
                disabled={!canSave || savePlan.isPending}
                className="shrink-0 rounded-md bg-brand-700 px-2.5 py-1 text-xs font-medium text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
              >
                {savePlan.isPending ? "Saving…" : "Save plan"}
              </button>
            </div>
            {recommendations.length > 0 && (
              <div className="border-b border-hairline px-3 py-2">
                <div className="flex items-center gap-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-700">
                  <Sparkles className="h-3 w-3" /> Pegasus recommends
                </div>
                <div className="space-y-1.5">
                  {recommendations.slice(0, 3).map((rec, i) => (
                    <div
                      key={i}
                      className={cn(
                        "flex items-start gap-2 rounded-md p-1.5 text-[11px] leading-snug",
                        rec.kind === "flag" ? "bg-amber-50" : "bg-slate-50",
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-slate-800">{rec.text}</div>
                        {rec.kind === "move" && (
                          <div className="text-slate-500">{rec.why}</div>
                        )}
                      </div>
                      {rec.kind === "reorder" && (
                        <button
                          type="button"
                          onClick={applyOptimized}
                          className="shrink-0 rounded bg-brand-700 px-2 py-0.5 font-medium text-white transition-colors hover:bg-brand-800"
                        >
                          Apply
                        </button>
                      )}
                      {rec.kind === "move" && (
                        <button
                          type="button"
                          onClick={() =>
                            setPendingMove({
                              dispatch: rec.dispatch,
                              label: rec.label,
                              fromEmpno: techFilter,
                              fromName: nameFor(techFilter),
                              toEmpno: rec.toEmpno,
                              toName: rec.toName,
                              savingMiles: rec.savingMiles,
                            })
                          }
                          className="shrink-0 rounded bg-brand-700 px-2 py-0.5 font-medium text-white transition-colors hover:bg-brand-800"
                        >
                          Review
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 border-b border-hairline px-3 py-2">
              <button
                type="button"
                onClick={applyPlanned}
                className={cn(
                  "rounded-md border px-2 py-0.5 text-xs font-medium transition-colors",
                  matchesPlanned
                    ? "border-slate-300 bg-slate-100 text-slate-900"
                    : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                Planned
              </button>
              <button
                type="button"
                onClick={applyOptimized}
                disabled={optimizedIds.length === 0}
                className={cn(
                  "rounded-md border px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-40",
                  matchesOptimized
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                    : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                Optimize
              </button>
              <span className="ml-auto text-[11px] text-slate-500">
                {savedMiles > 0.1
                  ? `saves ~${savedMiles.toFixed(1)} mi · ${Math.round(
                      savedMin,
                    )} min`
                  : "already efficient"}
              </span>
            </div>
            {realDrive && realDrive.complete && realDrive.totalMiles > 0.1 && (
              <div className="flex items-center gap-1.5 border-b border-hairline bg-emerald-50/50 px-3 py-1.5 text-[11px] text-slate-700">
                <span className="font-semibold text-emerald-700">
                  Actual driving:
                </span>
                ~{realDrive.totalMiles.toFixed(1)} mi · ~
                {Math.round(realDrive.totalMinutes)} min
                {routedWaypoints.length > 0 && (
                  <span className="text-slate-400">
                    · incl. {routedWaypoints.length}{" "}
                    {routedWaypoints.length === 1 ? "stop" : "stops"} for
                    meetings/errands
                  </span>
                )}
              </div>
            )}
            <div
              ref={listRef}
              onPointerMove={onDragMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              className="min-h-0 flex-1 select-none space-y-1 overflow-auto p-2"
            >
              {workingOrder.map((d, i) => {
                const s = stopById.get(d);
                if (!s) return null;
                const isSel =
                  (s.kind === "esc" && selected?.dispatch === d) ||
                  (s.kind === "pegasus" &&
                    !!selectedTicket &&
                    `p-${selectedTicket.id}` === d);
                const timeWindow = s.window;
                const priority = s.priority;
                const wc = timeWindow ? windowColorClasses(timeWindow) : null;
                const pc = priority ? windowColorClasses(priority) : null;
                const outline = wc ?? pc; // color the row by its window/code
                const isPinned = pinned.has(d);
                const cands = moveFor === d ? moveCandidates(s) : [];
                return (
                  <div key={d}>
                    <div
                      data-stopid={d}
                      className={cn(
                        "flex items-center gap-1 rounded-md border-2 px-1.5 py-1 transition-shadow",
                        outline ? outline.border : "border-hairline",
                        isSel
                          ? "bg-brand-50"
                          : isPinned
                            ? "bg-amber-50"
                            : "bg-white",
                        dragId === d
                          ? "pointer-events-none scale-[1.02] opacity-95 shadow-e2"
                          : isSel && "ring-2 ring-brand-200",
                      )}
                    >
                    <span
                      onPointerDown={(e) => startDrag(e, d)}
                      aria-label="Drag to reorder"
                      className="shrink-0 cursor-grab touch-none text-slate-300 transition-colors hover:text-slate-500 active:cursor-grabbing"
                    >
                      <GripVertical className="h-4 w-4" />
                    </span>
                    <span className="w-4 shrink-0 text-center text-xs font-bold tabular-nums text-slate-700">
                      {i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        s.esc ? onSelect(s.esc) : onSelectTicket(s.peg ?? null)
                      }
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex items-center gap-1 truncate text-xs font-medium text-slate-900">
                        {s.name}
                        {s.kind === "pegasus" && (
                          <Sparkles className="h-3 w-3 shrink-0 text-brand-500" />
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-slate-500">
                        {timeWindow && wc && (
                          <span
                            className={cn(
                              "rounded px-1 font-semibold",
                              wc.bg,
                              wc.text,
                            )}
                          >
                            {timeWindow}
                          </span>
                        )}
                        {priority && pc && (
                          <span
                            className={cn(
                              "rounded px-1 font-semibold uppercase",
                              pc.bg,
                              pc.text,
                            )}
                          >
                            {priority}
                          </span>
                        )}
                        <span className="truncate">
                          {[s.clock, s.city].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMoveFor(moveFor === d ? null : d)}
                      aria-label="Move to another tech"
                      title="Move to another tech"
                      className={cn(
                        "shrink-0 transition-colors",
                        moveFor === d
                          ? "text-brand-600"
                          : "text-slate-300 hover:text-slate-500",
                      )}
                    >
                      <ArrowRightLeft className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => togglePin(d)}
                      aria-label={isPinned ? "Unpin" : "Pin in place"}
                      title={
                        isPinned
                          ? "Pinned — stays put when you optimize"
                          : "Pin in place"
                      }
                      className={cn(
                        "shrink-0 transition-colors",
                        isPinned
                          ? "text-amber-500"
                          : "text-slate-300 hover:text-slate-500",
                      )}
                    >
                      <Pin
                        className={cn(
                          "h-3.5 w-3.5",
                          isPinned && "fill-amber-500",
                        )}
                      />
                    </button>
                    <div className="flex shrink-0 flex-col">
                      <button
                        type="button"
                        onClick={() => moveStop(d, -1)}
                        disabled={i === 0}
                        aria-label="Move earlier"
                        className="text-slate-400 transition-colors hover:text-slate-700 disabled:opacity-30"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStop(d, 1)}
                        disabled={i === workingOrder.length - 1}
                        aria-label="Move later"
                        className="text-slate-400 transition-colors hover:text-slate-700 disabled:opacity-30"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    </div>
                    {moveFor === d && (
                      <div className="mt-1 rounded-md border border-hairline bg-white p-1.5 shadow-e1">
                        <div className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          Move to — cheapest first
                        </div>
                        {cands.length === 0 ? (
                          <div className="px-1.5 py-1 text-xs text-slate-400">
                            No other techs to move to today.
                          </div>
                        ) : (
                          cands.map((c) => (
                            <button
                              key={c.empno}
                              type="button"
                              onClick={() => {
                                // Manual override still goes through the review
                                // preview — every cross-tech move gets a look.
                                setPendingMove({
                                  dispatch: d,
                                  label: s.name,
                                  fromEmpno: techFilter,
                                  fromName: nameFor(techFilter),
                                  toEmpno: c.empno,
                                  toName: c.name,
                                  savingMiles: 0,
                                });
                                setMoveFor(null);
                              }}
                              className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-xs transition-colors hover:bg-slate-50"
                            >
                              <span
                                className="h-2 w-2 shrink-0 rounded-full"
                                style={{ backgroundColor: c.color }}
                              />
                              <span className="min-w-0 flex-1 truncate text-slate-700">
                                {c.name}
                              </span>
                              <span className="shrink-0 tabular-nums text-slate-500">
                                +{c.cost.toFixed(1)} mi
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {pendingMove && (
        <MovePreview
          move={pendingMove}
          rows={baseRows}
          recoTechs={recoTechs}
          pending={setAssignment.isPending || moveDispatch.isPending}
          onClose={() => setPendingMove(null)}
          onConfirm={(targetOrder, editedOrder) => {
            // The moved call writes to the right place: a Pegasus job updates its
            // assigned_empno (useMoveDispatch); an ESC stop writes the read-only
            // override (dispatch_assignment). Then optionally persist the order.
            const done = () => {
              if (editedOrder) {
                savePlan.mutate({
                  date,
                  tech: pendingMove.toEmpno,
                  sequence: targetOrder.map((d) => ({ d, pin: false })),
                });
              }
              setPendingMove(null);
            };
            if (pendingMove.dispatch.startsWith("p-")) {
              moveDispatch.mutate(
                {
                  id: pendingMove.dispatch.slice(2),
                  empno: pendingMove.toEmpno,
                },
                { onSuccess: done },
              );
            } else {
              setAssignment.mutate(
                { dispatch: pendingMove.dispatch, tech: pendingMove.toEmpno },
                { onSuccess: done },
              );
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Cockpit recommendations strip (BOARD-COCKPIT-RECS-01) — the "read or click"
 * AI breakdown Seth asked for. When the dispatcher filters the cockpit to one
 * tech (and hasn't drilled into a specific stop), the roomy full-width bottom
 * strip fills with plain-English route recommendations + one-click actions:
 *   - "Re-run this route in a tighter order — saves ~X mi"  [Apply]
 *   - "Hand <stop> to <tech> — saves ~Y mi · he's qualified + nearby" [Move]
 *   - a skill flag if the assigned tech isn't capable of a call.
 * Deliberately simple: the dispatcher reads, then clicks. The fine-grained
 * drag/pin editor lives in Map view for when they want manual control.
 */
function RecommendationsStrip({
  boardMode = "service",
  rows,
  date,
  techFilter,
}: {
  boardMode?: BoardMode;
  rows: Dispatch[];
  date: string;
  techFilter: string;
}) {
  const hasCoord = (r: Dispatch) =>
    usableCoords(r.loc_latitude, r.loc_longitude);
  const baseRows = rows.filter((r) => !isPseudoDispatch(r));
  // Pegasus jobs for the day fold into the SAME route inputs here too, so the
  // cockpit strip's mileage + suggestions count them. (ROUTE-UNIFY)
  const pegByCoord = (useScheduledForBoard(date).data ?? []).filter((j) =>
    usableCoords(j.lat, j.lng),
  );
  const rosterTechs = useActiveTechs(DEPT_FOR_MODE[boardMode]).data;
  const nameFor = (empno: string) => {
    const t = rosterTechs?.find((x) => x.empno === empno);
    if (t) {
      const rn = t.empname ?? `${t.firstname ?? ""} ${t.lastname ?? ""}`.trim();
      if (rn && rn.length) return rn;
    }
    const r = baseRows.find((x) => x.tech_empno === empno);
    const n =
      r?.tech_name ?? `${r?.tech_first ?? ""} ${r?.tech_last ?? ""}`.trim();
    return n && n.length ? n : empno;
  };

  // Same recommendation inputs the Map editor builds, recomputed here so the
  // strip is fully self-contained (no plumbing through the cockpit layout).
  const techSkillsData = useTechSkills().data;
  const locationNotes = useLocationNotes(baseRows.map((r) => r.custno)).data;
  const baseRowsSig = baseRows
    .map((r) => `${r.dispatch}:${r.tech_empno}`)
    .join(",");
  const locNotesSig = Object.keys(locationNotes ?? {}).sort().join(",");
  const rosterSig = (rosterTechs ?? []).map((t) => t.empno).join(",");
  const pegSig = pegByCoord
    .map((j) => `p-${j.id}:${j.assigned_empno}`)
    .join(",");
  const recoTechs = useMemo<RecoTech[]>(() => {
    const rosterMap = new Map<string, string | null | undefined>();
    for (const t of rosterTechs ?? []) rosterMap.set(t.empno, t.firstname);
    for (const r of baseRows)
      if (!rosterMap.has(r.tech_empno)) rosterMap.set(r.tech_empno, r.tech_first);
    const roster = [...rosterMap].map(([empno, first]) => ({ empno, first }));
    const byTech = new Map<string, RecoTech["stops"]>();
    for (const r of baseRows) {
      if (!hasCoord(r)) continue;
      const locNote = locationNotes?.[`${r.custno}|${r.locno}`] ?? "";
      const combinedNotes = `${r.notes ?? ""}\n${locNote}`;
      const excludedEmpnos = deriveExcludedTechs(combinedNotes, roster);
      let reqEmpno = deriveRequestedTech(combinedNotes, roster);
      // A tech the notes explicitly EXCLUDE can never be the "requested" tech —
      // belt-and-suspenders for "Do not send Camron" being read as a request.
      if (reqEmpno && excludedEmpnos.includes(reqEmpno)) reqEmpno = null;
      const arr = byTech.get(r.tech_empno) ?? [];
      arr.push({
        dispatch: r.dispatch,
        lat: r.loc_latitude as number,
        lng: r.loc_longitude as number,
        label: displayCustomerName(r.locname, r.customer_fullname),
        city: r.city,
        band: timeBand(r),
        requiredSkills: deriveRequiredSkills(r.notes),
        requestedEmpno: reqEmpno,
        requestedName: reqEmpno ? rosterMap.get(reqEmpno) ?? null : null,
        requestedEmpnos: deriveRequestedTechs(combinedNotes, roster).filter((e) => !excludedEmpnos.includes(e)),
        excludedEmpnos,
        attrLock: deriveAttrLock(combinedNotes),
      });
      byTech.set(r.tech_empno, arr);
    }
    // Pegasus jobs join the same per-tech route inputs (ROUTE-UNIFY).
    for (const j of pegByCoord) {
      if (!j.assigned_empno) continue;
      const arr = byTech.get(j.assigned_empno) ?? [];
      arr.push(pegRecoStop(j, roster, rosterMap));
      byTech.set(j.assigned_empno, arr);
    }
    return [...byTech.entries()].map(([empno, stops]) => ({
      empno,
      name: nameFor(empno),
      skills: new Set(techSkillsData?.[empno] ?? []),
      stops,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRowsSig, techSkillsData, locNotesSig, rosterSig, pegSig]);

  // Gate on skills loading — computing before they arrive would show false
  // "not qualified" flags (every tech would look skill-less).
  const recommendations = useMemo(
    () => (techSkillsData ? recommendForTech(techFilter, recoTechs) : []),
    [techFilter, recoTechs, techSkillsData],
  );

  const { data: savedPlan } = useRoutePlan(date, techFilter);
  const savePlan = useSaveRoutePlan();
  const setAssignment = useSetAssignment();
  const moveDispatch = useMoveDispatch(); // Pegasus cross-tech move
  // Reassign the moved call to the right place: Pegasus job → assigned_empno;
  // ESC stop → the read-only dispatch_assignment override. (ROUTE-UNIFY)
  const reassign = (
    dispatch: string,
    toEmpno: string,
    opts?: { onSuccess?: () => void },
  ) => {
    if (dispatch.startsWith("p-")) {
      moveDispatch.mutate({ id: dispatch.slice(2), empno: toEmpno }, opts);
    } else {
      setAssignment.mutate({ dispatch, tech: toEmpno }, opts);
    }
  };

  // Cross-tech moves go through a review/compare step (no silent reassign), with
  // an undo after committing — Seth: "it just does it, no undo."
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [pendingReorder, setPendingReorder] = useState<PendingReorder | null>(
    null,
  );
  const [lastMove, setLastMove] = useState<{
    dispatch: string;
    fromEmpno: string;
    label: string;
    toName: string;
  } | null>(null);

  const techRec = recoTechs.find((t) => t.empno === techFilter);
  const stopCount = techRec?.stops.length ?? 0;

  // The "tighter order" recommendation opens a review/compare preview (it no
  // longer saves on click) — Seth: "I'd like a preview before it just applies."
  const openReorderPreview = () => {
    if (!techRec || techRec.stops.length < 2) return;
    const proposedOrder = optimizeBanded(techRec.stops).map((s) => s.dispatch);
    const idset = new Set(techRec.stops.map((s) => s.dispatch));
    // Current = the tech's effective order TODAY, covering ALL stops: the saved
    // plan order (filtered to today's stops) plus any stops not in the plan,
    // appended in planned-time order. Without the append, a partial saved plan
    // compares the wrong stop count (and the miles lie).
    const plannedIdsByBand = [...techRec.stops]
      .sort((a, b) => a.band - b.band)
      .map((s) => s.dispatch);
    let currentOrder: string[];
    if (savedPlan && savedPlan.length) {
      const ordered = savedPlan.map((s) => s.d).filter((d) => idset.has(d));
      const inPlan = new Set(ordered);
      currentOrder = [...ordered, ...plannedIdsByBand.filter((d) => !inPlan.has(d))];
    } else {
      currentOrder = plannedIdsByBand;
    }
    setPendingReorder({
      techEmpno: techFilter,
      techName: nameFor(techFilter),
      stops: techRec.stops,
      currentOrder,
      proposedOrder,
    });
  };
  // Save the order the dispatcher confirmed (possibly hand-tweaked in the
  // preview), keeping any pins they'd set in Map view.
  const commitReorder = (order: string[]) => {
    const pinned = new Set(
      (savedPlan ?? []).filter((s) => s.pin).map((s) => s.d),
    );
    savePlan.mutate(
      {
        date,
        tech: techFilter,
        sequence: order.map((d) => ({ d, pin: pinned.has(d) })),
      },
      { onSuccess: () => setPendingReorder(null) },
    );
  };

  // Look up a recommended call's full row so the card can show its context
  // (time window, type, a notes preview) — Seth: "give me a little more context."
  const byId = new Map(rows.map((r) => [r.dispatch, r] as const));

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2.5 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-brand-700" />
        <h3 className="text-sm font-semibold text-slate-900">
          {nameFor(techFilter)}&apos;s route — Pegasus recommends
        </h3>
        <span className="text-xs text-slate-400">
          {stopCount} mapped stop{stopCount === 1 ? "" : "s"}
        </span>
      </div>

      {!techSkillsData ? (
        <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-hairline px-4 text-center text-sm text-slate-400">
          Checking skills and routes…
        </div>
      ) : recommendations.length === 0 ? (
        <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-hairline px-4 text-center text-sm text-slate-500">
          {stopCount < 2
            ? "Not enough mapped stops to optimize this route yet."
            : "This route already looks efficient — nothing to change right now."}
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {recommendations.slice(0, 6).map((rec, i) => (
            <div
              key={i}
              className={cn(
                "flex items-start justify-between gap-2.5 rounded-lg border p-2.5 text-xs leading-snug",
                rec.kind === "flag"
                  ? "border-amber-200 bg-amber-50"
                  : "border-hairline bg-slate-50",
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800">{rec.text}</div>
                {rec.kind === "move" && (
                  <div className="mt-0.5 text-slate-500">{rec.why}</div>
                )}
                {(rec.kind === "move" || rec.kind === "flag") &&
                  (() => {
                    const r = byId.get(rec.dispatch);
                    if (!r) return null;
                    const { timeWindow, priority } = resolveWindowAndPriority(
                      r.window_code,
                      r.notes,
                    );
                    return (
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                        {timeWindow && (
                          <span className="rounded bg-slate-200 px-1 font-semibold text-slate-600">
                            {timeWindow}
                          </span>
                        )}
                        {priority && (
                          <span className="rounded bg-slate-200 px-1 font-semibold uppercase text-slate-600">
                            {priority}
                          </span>
                        )}
                        {r.tpromtime && (
                          <span className="tabular-nums">
                            {formatTime(r.tpromtime)}
                          </span>
                        )}
                        <span className="truncate">
                          {extractJobPreview(r.notes, 44)}
                        </span>
                      </div>
                    );
                  })()}
              </div>
              {rec.kind === "reorder" && (
                <button
                  type="button"
                  onClick={openReorderPreview}
                  className="shrink-0 rounded-md bg-brand-700 px-2.5 py-1 font-medium text-white transition-colors hover:bg-brand-800"
                >
                  Review
                </button>
              )}
              {rec.kind === "move" && (
                <button
                  type="button"
                  onClick={() =>
                    setPendingMove({
                      dispatch: rec.dispatch,
                      label: rec.label,
                      fromEmpno: techFilter,
                      fromName: nameFor(techFilter),
                      toEmpno: rec.toEmpno,
                      toName: rec.toName,
                      savingMiles: rec.savingMiles,
                    })
                  }
                  className="shrink-0 rounded-md bg-brand-700 px-2.5 py-1 font-medium text-white transition-colors hover:bg-brand-800"
                >
                  Review move
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {lastMove && (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800">
          <span className="min-w-0 flex-1 truncate">
            Moved <span className="font-medium">{lastMove.label}</span> to{" "}
            {lastMove.toName}.
          </span>
          <button
            type="button"
            onClick={() => {
              reassign(lastMove.dispatch, lastMove.fromEmpno);
              setLastMove(null);
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-300 bg-white px-2 py-0.5 font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
          >
            <RotateCcw className="h-3 w-3" /> Undo
          </button>
        </div>
      )}

      {pendingMove && (
        <MovePreview
          move={pendingMove}
          rows={rows}
          recoTechs={recoTechs}
          pending={setAssignment.isPending || moveDispatch.isPending}
          onClose={() => setPendingMove(null)}
          onConfirm={(targetOrder, editedOrder) =>
            reassign(pendingMove.dispatch, pendingMove.toEmpno, {
              onSuccess: () => {
                if (editedOrder) {
                  savePlan.mutate({
                    date,
                    tech: pendingMove.toEmpno,
                    sequence: targetOrder.map((d) => ({ d, pin: false })),
                  });
                }
                setLastMove({
                  dispatch: pendingMove.dispatch,
                  fromEmpno: pendingMove.fromEmpno,
                  label: pendingMove.label,
                  toName: pendingMove.toName,
                });
                setPendingMove(null);
              },
            })
          }
        />
      )}

      {pendingReorder && (
        <ReorderPreview
          pendingReorder={pendingReorder}
          rows={rows}
          pending={savePlan.isPending}
          onClose={() => setPendingReorder(null)}
          onConfirm={(order) => commitReorder(order)}
        />
      )}
    </div>
  );
}

/**
 * Cockpit layout (BOARD-COCKPIT-01) — modeled on ESC's old MapPoint board:
 * tech columns + map on the top row, with a full-width detail/notes strip
 * across the bottom that fills when you select a stop (from a column card OR a
 * map pin — one shared selection). The bottom strip is the future home for
 * inline note editing (BOARD-NOTES-EDIT-01).
 */
function BoardCockpitView({
  boardMode = "service",
  rows,
  date,
  activeTechs,
  techFilter,
  selected,
  selectedTicket,
  onSelect,
  onSelectTicket,
  onToggleTech,
  availByTech,
  onAddAvailability,
  onEditAvailability,
  onRebuildDay,
  density = "cards",
  viewMode = "active",
  showMap = true,
  pendingOptimize = false,
  onOptimizeConsumed,
}: {
  boardMode?: BoardMode;
  viewMode?: ViewMode;
  rows: Dispatch[];
  date: string;
  activeTechs: ReturnType<typeof useActiveTechs>["data"];
  techFilter: string | null;
  selected: Dispatch | null;
  selectedTicket: PendingTicket | null;
  onSelect: (d: Dispatch | null) => void;
  onSelectTicket: (t: PendingTicket | null) => void;
  onToggleTech: (empno: string) => void;
  availByTech?: Map<string, { out: AvailEvent | null; timed: AvailEvent[] }>;
  onAddAvailability?: (empno: string) => void;
  onEditAvailability?: (e: AvailEvent) => void;
  onRebuildDay?: (empno: string) => void;
  density?: Density;
  /** COCKPIT-WINDOW-01: false when the map rides in its own window — the
   *  cockpit keeps its strip + bottom detail bar, just without the inline
   *  map pane (the external window syncs selections back here). */
  showMap?: boolean;
  pendingOptimize?: boolean;
  onOptimizeConsumed?: () => void;
}) {
  // Resizable bottom strip — drag the handle up/down; height is remembered.
  const [stripH, setStripH] = useState<number>(loadCockpitHeight);
  useEffect(() => {
    try {
      localStorage.setItem(COCKPIT_HEIGHT_KEY, String(stripH));
    } catch {
      // ignore
    }
  }, [stripH]);
  // BOARD-SHORT-VIEWPORT (review 2026-11-02): the remembered height is a WISH,
  // not a right. On a short screen (1024×641 in the review walk) a 600 px strip
  // ate the whole cockpit and the lanes pane — flex-1 min-h-0 — rendered zero
  // lanes. The strip may never take more than half of the cockpit's height, so
  // the lanes always keep at least half. The wish is kept as-is; on a tall
  // monitor the same login gets its full strip back.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number>(0);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height ?? 0;
      setRootH(Math.round(h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const maxStrip = rootH > 0 ? Math.max(120, Math.floor(rootH * 0.5)) : 600;
  const stripShown = Math.min(stripH, maxStrip);

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = stripShown;
    const onMove = (ev: PointerEvent) => {
      // Drag up → taller strip (never past half the cockpit — see maxStrip).
      const next = startH + (startY - ev.clientY);
      setStripH(Math.min(Math.min(600, maxStrip), Math.max(140, next)));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div ref={rootRef} className="flex h-full flex-col">
      {/* Top row: columns (left) + map (right), as in the ESC MapPoint board.
          The columns pane scrolls BOTH ways — on short screens the lanes are
          taller than the pane and the lower cards must stay reachable. */}
      <div className="flex min-h-0 flex-1 gap-3">
        {/* COCKPIT-FIT: the fitWidth BoardBody grid already fits the whole
            roster on one row at desktop widths (no scrollbar appears). Keep
            overflow-x AUTO — not hidden — so a genuinely narrow screen scrolls
            to reach the last techs instead of clipping them out of reach. */}
        <div className="min-w-0 flex-1 overflow-x-auto overflow-y-auto">
          <BoardBody
            boardMode={boardMode}
            viewMode={viewMode}
            rows={rows}
            date={date}
            activeTechs={activeTechs}
            techFilter={techFilter}
            onSelect={onSelect}
            onSelectTicket={onSelectTicket}
            availByTech={availByTech}
            onAddAvailability={onAddAvailability}
            onEditAvailability={onEditAvailability}
            onRebuildDay={onRebuildDay}
            density={density}
            fitWidth
            onFilterTech={onToggleTech}
          />
        </div>
        {showMap && (
          <div className="w-[34%] min-w-[300px] shrink-0">
            <BoardMapView
              boardMode={boardMode}
              viewMode={viewMode}
              rows={rows}
              date={date}
              techFilter={techFilter}
              selected={selected}
              selectedTicket={selectedTicket}
              onSelect={onSelect}
              onSelectTicket={onSelectTicket}
              onToggleTech={onToggleTech}
              compact
              pendingOptimize={pendingOptimize}
              onOptimizeConsumed={onOptimizeConsumed}
            />
          </div>
        )}
      </div>

      {/* Drag handle to resize the detail strip (remembered) */}
      <div
        onPointerDown={startResize}
        data-no-dblclick
        className="group flex h-3 shrink-0 cursor-row-resize items-center justify-center"
        title="Drag to resize"
      >
        <div className="h-1 w-10 rounded-full bg-slate-300 transition-colors group-hover:bg-slate-400" />
      </div>

      {/* Bottom: full-width detail / notes strip for the selected stop */}
      <div
        style={{ height: stripShown }}
        data-no-dblclick
        className="shrink-0 overflow-auto rounded-xl border border-hairline bg-white p-4 shadow-e1"
      >
        {selected ? (
          <DispatchDetailBody
            dispatch={selected}
            onClose={() => onSelect(null)}
            showHeader
            variant="strip"
          />
        ) : selectedTicket ? (
          selectedTicket.visit_role === "install" && selectedTicket.job_id ? (
            <JobBoardOverview job={selectedTicket} onClose={() => onSelectTicket(null)} />
          ) : (
            <PegasusJobDetailBody
              job={selectedTicket}
              onClose={() => onSelectTicket(null)}
              showHeader
              variant="strip"
            />
          )
        ) : techFilter ? (
          <RecommendationsStrip boardMode={boardMode} rows={rows} date={date} techFilter={techFilter} />
        ) : (
          <div className="flex h-full items-center justify-center text-center text-sm text-slate-500">
            Pick a stop or a tech for details.
          </div>
        )}
      </div>
    </div>
  );
}

function BoardStats({
  rows,
  techFilter,
  techCount,
}: {
  rows: Dispatch[];
  techFilter: string | null;
  /** BOARD-PCT-01: how many techs could take work today (1 when filtered). */
  techCount: number;
}) {
  // BOARD-PCT-01 (the one gap from the BoardAfter audit, built on Seth's
  // "go with your recommendations"): % booked = today's stops ÷ (working
  // techs × full calls per tech-day). The per-tech number is an org
  // setting (Settings → AI Controls, default 6) so the definition is
  // Seth's to tune — the tooltip shows the whole formula, no mystery math.
  const perTech = useOrgSettingNumber("board_capacity_calls_per_tech", 6).data ?? 6;
  // If filtered to a single tech, stats reflect just that lane
  const filtered = techFilter
    ? rows.filter((r) => r.tech_empno === techFilter)
    : rows;

  let done = 0;
  let active = 0;
  let upcoming = 0;
  const techs = new Set<string>();
  for (const r of filtered) {
    techs.add(r.tech_empno);
    if (STATUS_DONE.has(r.status ?? "")) done++;
    else if (STATUS_ACTIVE.has(r.status ?? "")) active++;
    else if (STATUS_PENDING.has(r.status ?? "") || !r.status) upcoming++;
  }
  const capTechs = Math.max(techCount, techs.size);
  const capacity = capTechs * perTech;
  const pctBooked = capacity > 0 ? Math.round((filtered.length / capacity) * 100) : null;

  // BOARD-HD: the day's score as real chips — a colored dot, a big number, a
  // plain word — instead of loose text floating in the header.
  return (
    <div className="flex flex-wrap items-center gap-3">
      <StatChip label="stops" value={filtered.length} />
      <StatChip label="techs" value={techs.size} />
      <StatChip label="done" value={done} accent="success" />
      <StatChip label="active" value={active} accent="info" />
      <StatChip label="to go" value={upcoming} accent="warn" />
      {pctBooked != null && (
        <span
          className="inline-flex items-baseline gap-1 px-1"
          title={`${filtered.length} stops against ${capTechs} tech${capTechs === 1 ? "" : "s"} × ${perTech} calls each (${capacity}) — tune "Full calls per tech-day" in Settings → AI Controls`}
        >
          <span className={cn("text-[15px] font-bold tabular-nums leading-none", pctBooked >= 95 ? "text-amber-700" : "text-slate-900")}>{pctBooked}%</span>
          <span className="text-[11.5px] font-medium leading-none text-slate-500">booked</span>
        </span>
      )}
    </div>
  );
}

function StatChip({
  label,
  value,
  accent: _accent,
}: {
  label: string;
  value: number;
  accent?: "success" | "info" | "warn";
}) {
  // DESIGN-CALM-01 Wave 2: the day's score is plain numbers — no pills, no
  // colored dots. The one colored thing up here is "carried over", and only
  // when it is not zero.
  return (
    <span className="inline-flex items-baseline gap-1 px-1">
      <span className="text-[15px] font-bold tabular-nums leading-none text-slate-900">{value}</span>
      <span className="text-[11.5px] font-medium leading-none text-slate-500">{label}</span>
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="h-96 w-64 flex-shrink-0 animate-pulse rounded-xl bg-slate-200"
        />
      ))}
    </div>
  );
}

function BoardModeToggle({ value, onChange }: { value: BoardMode; onChange: (v: BoardMode) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as BoardMode)}
      title="Filter the board by work type"
      className="h-8 rounded-full border border-hairline bg-slate-50 pl-3 pr-2 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-100 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
    >
      <option value="service">Service</option>
      <option value="install">Install</option>
      <option value="plumbing">Plumbing</option>
      <option value="nc">New Construction</option>
    </select>
  );
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const reduce = useReducedMotion();
  const options: { key: ViewMode; label: string }[] = [
    { key: "active", label: "Active" },
    { key: "history", label: "History" },
    { key: "all", label: "All" },
  ];
  return (
    <div className="inline-flex h-8 items-center rounded-full border border-hairline bg-slate-50 p-0.5">
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className={cn(
              "relative flex h-full items-center rounded-full px-3 text-[12.5px] font-semibold transition-colors duration-150 ease-out-quart",
              active ? "text-slate-900" : "text-slate-500 hover:text-slate-900",
            )}
          >
            {active && (
              <motion.span
                layoutId="board-viewmode-pill"
                className="absolute inset-0 rounded-full bg-white shadow-e1"
                transition={reduce ? { duration: 0 } : { duration: 0.2, ease: EASE_OUT_EXPO }}
              />
            )}
            <span className="relative z-10">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Compact (ESC-style whole-board) vs Cards (rich) — persisted per user. */
function DensityToggle({
  value,
  onChange,
}: {
  value: Density;
  onChange: (v: Density) => void;
}) {
  const options: { key: Density; label: string; title: string }[] = [
    { key: "compact", label: "Compact", title: "ESC-style color blocks — the whole board at a glance" },
    { key: "cards", label: "Cards", title: "Richer cards with pills + job preview" },
  ];
  return (
    <div className="inline-flex h-8 items-center rounded-full border border-hairline bg-slate-50 p-0.5">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          title={opt.title}
          onClick={() => onChange(opt.key)}
          className={cn(
            "flex h-full items-center rounded-full px-3 text-[12.5px] font-semibold transition-colors duration-150 ease-out-quart",
            value === opt.key
              ? "bg-white text-slate-900 shadow-e1"
              : "text-slate-500 hover:text-slate-900",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** The map's three states — docked (cockpit), its own window, off. */
function MapModeToggle({
  value,
  onChange,
}: {
  value: MapMode;
  onChange: (v: MapMode) => void;
}) {
  const reduce = useReducedMotion();
  const options: { key: MapMode; label: string; title: string }[] = [
    { key: "docked", label: "Docked", title: "Map beside the board (cockpit)" },
    { key: "window", label: "Window", title: "Map in its own window — drag it to another monitor" },
    { key: "off", label: "Off", title: "Just the board" },
  ];
  return (
    <div className="inline-flex h-8 items-center rounded-full border border-hairline bg-slate-50 p-0.5">
      <MapIcon className="ml-2 mr-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            title={opt.title}
            className={cn(
              "relative flex h-full items-center rounded-full px-3 text-[12.5px] font-semibold transition-colors duration-150 ease-out-quart",
              active ? "text-slate-900" : "text-slate-500 hover:text-slate-900",
            )}
          >
            {active && (
              <motion.span
                layoutId="board-map-pill"
                className="absolute inset-0 rounded-full bg-white shadow-e1"
                transition={reduce ? { duration: 0 } : { duration: 0.2, ease: EASE_OUT_EXPO }}
              />
            )}
            <span className="relative z-10">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
